import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
// Minimal lucide-react - only for icons not yet converted
// Most icons now use premium BespokeIcons
import { toast } from 'sonner';
import GrantitudeSpiral from './GrantitudeSpiral';
import GrantSteampunkEngine from './GrantSteampunkEngine';
import PurposeIntro from './PurposeIntro';
import LiabilityDisclaimer, { hasAcceptedDisclaimer, acceptDisclaimer } from './LiabilityDisclaimer';
import Credits from './Credits';
import { GrayWordsPreflection, GrayWordsInlineAnalyzer, analyzeForGrayWords, GRANDMOTHER_WISDOM } from './GrayWordsAnalyzer';
import FlowBreadcrumb from './FlowBreadcrumb';
import WisdomGuide from './WisdomGuide';
import SpiralCouncilSOS from './SpiralCouncilSOS';
import { WisdomPopup, useWisdomManager } from './NVCWisdomPopups';
import { CosmicArtInstallation, CONTEMPLATION_IMAGES } from './CosmicArtInstallation';
import { HonestyEmpathyDance, DanceIndicator, ReflectionExchange } from './HonestyEmpathyDance';
import { SoulReminderBanner, useSoulReminder, SoulReminderInline } from './SoulReminder';
import { ChapterTransition } from './ChapterTransition';
import VisionQuest from './VisionQuest';
import { NeedsSongDialog } from './NeedsSongGenerator';
import { 
  TimeoutSOS, SOSTriggerButton, StayPresentReminder, 
  RolePlaySuggestion, InstantSummary, SummaryTriggerButton 
} from './MindfulSOS';
import FeelingsNeedsHelper from './FeelingsNeedsHelper';
import IChooseToExercise from './IChooseToExercise';
import ObservationsExercise from './ObservationsExercise';
import EmpathySOS from './EmpathySOS';
import AdminFeedbackButton from './AdminFeedbackButton';
import ElchmEProjectFooter from './ElchmEProjectFooter';
import SpiralInterchange from './SpiralInterchange';
import OFNRProcessHelper from './OFNRProcessHelper';
import { BeforeWeBegin, VisionQuestVideoIntro } from './BeforeWeBegin';
import { GoldenSummary, GoldenSummaryButton } from './GoldenSummary';
import PracticeCards from './PracticeCards';
import SoulScoreWidget from './SoulScoreWidget';
import { 
  ObserveIcon, FeelIcon, NeedIcon, GoldIcon, 
  HomeIcon, SparkleIcon, PlayIcon, RefreshIcon,
  ArrowIcon, PlusIcon, TrashIcon, MessageIcon, ClockIcon,
  ProfileIcon, CalendarIcon, BookIcon, StarIcon,
  SaveIcon, UsersIcon, PenIcon, PauseIcon, VolumeIcon,
  ShareIcon, CopyIcon, CheckIcon, CloseIcon, ChevronIcon,
  InfoIcon, UserPlusIcon, SearchIcon, MusicIcon, BridgeIcon,
  WisdomIcon, CircleOfSoulsIcon, HeartFilledIcon, AlertIcon,
  FireIcon, HeartIcon, CompassIcon, InsightIcon
} from './BespokeIcons';

// Golden Ratio for mathematical beauty
const PHI = 1.618033988749895;
const FIBONACCI = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];

// Conflict mode - full spectrum of feelings (challenging + positive)
const FEELING_CHIPS = [
  // Primary conflict feelings (first row - most prominent)
  'angry', 'frustrated', 'hurt', 'disappointed', 'confused', 'anxious',
  // Secondary conflict feelings
  'irritated', 'worried', 'scared', 'overwhelmed', 'sad', 'lonely',
  'defensive', 'resentful', 'exhausted', 'stressed', 'helpless', 'ashamed',
  // Positive feelings (also available in conflict for mixed emotions)
  'hopeful', 'relieved', 'curious', 'determined', 'peaceful', 'encouraged',
  'grateful', 'understood', 'heard', 'supported', 'safe', 'clear'
];

// Appreciation mode - feelings of gratitude and joy (positive first)
const APPRECIATION_FEELING_CHIPS = [
  // Primary appreciation feelings (first row - most prominent)
  'grateful', 'touched', 'moved', 'joyful', 'warm', 'peaceful',
  // Secondary appreciation feelings
  'delighted', 'inspired', 'energized', 'hopeful', 'content', 'fulfilled',
  'appreciated', 'valued', 'seen', 'connected', 'loved', 'cherished',
  'relieved', 'encouraged', 'uplifted', 'blessed', 'honored', 'light',
  // Deeper appreciation feelings
  'awe', 'wonder', 'reverence', 'tenderness', 'compassion', 'pride'
];

// Romance mode - feelings of intimacy and connection (desire + tenderness)
const ROMANCE_FEELING_CHIPS = [
  // Primary romance feelings (first row - most prominent)
  'desire', 'longing', 'tenderness', 'passion', 'cherished', 'adored',
  // Secondary romance feelings
  'vulnerable', 'connected', 'attracted', 'intimate', 'loving', 'devoted',
  'yearning', 'enchanted', 'aroused', 'safe', 'seen', 'desired',
  // Deeper romantic feelings
  'melting', 'surrendered', 'alive', 'electrified', 'worshiped', 'honored',
  'playful', 'adventurous', 'sensual', 'romantic', 'excited', 'curious',
  // Positive connection feelings
  'peaceful', 'content', 'fulfilled', 'grateful', 'blessed', 'complete'
];

const NEED_CHIPS = [
  'safety', 'respect', 'understanding', 'connection', 'autonomy', 
  'love', 'rest', 'play', 'meaning', 'belonging', 'honesty', 'trust',
  'peace', 'growth', 'acceptance', 'freedom'
];

// Appreciation mode - needs that were beautifully met
const APPRECIATION_NEED_CHIPS = [
  'connection', 'love', 'belonging', 'appreciation', 'contribution',
  'care', 'support', 'trust', 'celebration', 'community', 'growth',
  'meaning', 'beauty', 'harmony', 'presence', 'joy'
];

// Romance mode - needs for intimacy and romantic connection
const ROMANCE_NEED_CHIPS = [
  'intimacy', 'connection', 'romance', 'vulnerability', 'affection', 'touch',
  'desire', 'passion', 'tenderness', 'closeness', 'trust', 'safety',
  'to be seen', 'to be cherished', 'emotional connection', 'physical connection',
  'adventure', 'playfulness', 'devotion', 'commitment'
];

// Philosophy Primer Component
const PhilosophyPrimer = ({ isOpen, onClose }) => {
  const [revealed, setRevealed] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setRevealed(true), 100);
    } else {
      setRevealed(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sections = [
    {
      title: "Belong First",
      content: `Before anything else—before techniques, before strategies, before solutions—comes the willingness to belong to the other person for a moment.

By creating unmistakable, unquestionable, authentic concern and connection, something remarkable happens: the other person can't help but become empathetic to you.

This isn't about being nice or performing care. It's about genuinely opening yourself to their experience, their pain, their perspective—even when it's hard, especially when it's hard.

When you lead with this vulnerable belonging, the rest falls into place with practice. This is the golden thread that connects us all.`
    },
    {
      title: "The ElchmE",
      content: `This process, rooted in Nonviolent Communication and part of The Belonging Sessions, is about alchemizing conflict into solutions. It's taking seemingly impossible knots and working them into golden, impossibly beautiful bows.

Little hidden solutions where you essentially create a new reality by having a different kind of mindset.`
    },
    {
      title: "Relaxed Potency",
      content: `Fundamentally this is about achieving a state of relaxed potency—the ultimate interconnection with the love energy. Not because you're supposed to do this, but because it is the way we were meant to be connected to the universal energy of love between all things and the Earth itself.

When you are in this state of being, having what we might call compassion becomes the natural state of being. It takes effort and vulnerability, but it can be learned when you practice this with your family.`
    },
    {
      title: "Before You Begin",
      content: `Not by using techniques and special phrases, reading book after book—it really, really, really is just about getting out of your head, out of your thoughts, out of your emotions for just a moment.

First, before anything—do not proceed, do not go—until you are in a natural receptive state of understanding for yourself, your true Self, the person that you were communicating with, and all of your family, which would include the Earth itself.

This is not political or even really spiritual per se. It's just the basal way of being.`
    },
    {
      title: "The Waypoint Gate",
      content: `In this little game, this exercise, you input the problem, you bring in the people, and you play the game. You proceed into the state where we are temporarily freed from the servitude of the mind and the calculation and the history and the beliefs, and the perception, the assumptions, the absolute unquestionable "reality" of course, that we live in.

No. For this moment, we create a new waypoint gate—a state of mutual receptivity where we're simply open to understanding our true self, communicating with less emotion and pain, and genuinely, authentically, persistently, relentlessly, absolutely curious about the other person, as they attempt to do the same.`
    },
    {
      title: "Even If They Can't",
      content: `And even if they can't, and even if you are so right, so righteous—it doesn't matter. This isn't about justice or adjudicating.

This is about being like perhaps maybe Jesus was. With the homeless. Peaceful, meek, and real. An absolute state of love engaging with the wretched. Engaging with the torturer and executioner.

And so we are just saying: start with a vast embrace of love for the fragile parts of you. Get into the flow state with the other person. Be genuinely curious about their well-being.`
    },
    {
      title: "The Golden Rule",
      content: `It always comes back to this: do unto others as you would want done to you.

Try not to be overwhelmed with all of the details—at the end of the day, it's always about creating a mutual spiritual connection with another person first. Be relentless in this. This is the way that we can actually hear each other and communicate and achieve the results we are seeking.

Have these kinds of conversations with mutual tenderness regularly. Regularly clear out conflict before it hardens into resentment.

As Krishnamurti said: "The highest form of Human Intelligence is to observe yourself without judgement."`
    },
    {
      title: "Agency & Choice",
      content: `When we choose to do things, we are more likely to proceed. When we feel we must do things, we automatically tend to resist.

Our need for agency—the ability to choose our own path and make our own decisions—is fundamental. Even the way we speak matters: phrases like "I have to" or "you must" create resistance. Instead, recognize that every action is a choice.

Even so-called compliments can relate to being dominated by the external world versus being guided by our inner self. Our needs are related to the divine energy flowing through us.`
    },
    {
      title: "The Ripples",
      content: `By moralizing and judging, critiquing, advising, instructing, commanding, demanding, projecting—we make things worse. We create ripples. Sometimes permanently. Sometimes irrevocably damaging. Not only ourselves, but the full constellation of the species and all living things. In a way, a tragedy.

And so in lieu of this, simply proceed, starting with love. Again, not to be woo-woo, but because you will be able to be receptive with each other and likely achieve a much better outcome.`
    },
    {
      title: "The Colors",
      content: `Do the Blue—the oceanic observe. The Red and Green—relate to each other. The blood, the boiling, the energy, the emotions of what's inside of you, integrating. Directly a result of an unmet absolute requirement of your existence—the Green.

What do you need to grow most in this situation?

The understanding of the Red and Green in you and in them, from a point of view of absolute love and compassion for a moment—no matter how wretched—colors into Gold.

And when we all do this, we do this as a species, over the whole world. The Federation of Families.`
    },
    {
      title: "The Point",
      content: `The craziest thing of all is that love and compassion alchemize conflict and pain into Gold.

By negotiating, requesting, back-and-forth—you find perhaps the one definite resolution where everyone has some degree of benefit.

Can you imagine the ripples that are NOT created of darkness and pain because of this? Over and over again with every single conflict, potentially.

Isn't that the point of life? If you "win" the engagement, in the big picture you certainly quite often lose. And if you lose, you both lose.

Tragic.`
    }
  ];

  return (
    <div className={`fixed inset-0 z-50 transition-all duration-700 ${
      revealed ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Animated background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at 50% 50%, 
            rgba(212, 175, 55, 0.1) 0%, 
            rgba(0, 0, 0, 1) 70%)`,
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${FIBONACCI[i % 12] * 3}px`,
                height: `${FIBONACCI[i % 12] * 3}px`,
                left: `${(i * PHI * 15) % 100}%`,
                top: `${(i * PHI * 23) % 100}%`,
                background: `radial-gradient(circle, rgba(212, 175, 55, ${0.3 - i * 0.01}) 0%, transparent 70%)`,
                animation: `float ${5 + i}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="text-3xl font-light text-amber-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                The Way of ElchmE
              </h1>
              <p className="text-white/40 text-sm mt-1">A journey from knots to bows, from conflict to connection, guided by compassion.</p>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-white/40 hover:text-white hover:bg-white/10"
            >
              <CloseIcon size={20} color="#9CA3AF" />
            </Button>
          </div>

          {/* Section Navigation */}
          <div className="flex flex-wrap gap-2 mb-8">
            {sections.map((section, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSection(idx)}
                className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                  activeSection === idx 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                    : 'bg-white/5 text-white/40 border border-white/10 hover:border-amber-500/20'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Active Section */}
          <div className={`transition-all duration-500 ${revealed ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <Card className="bg-white/5 border-amber-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl text-amber-300" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {sections[activeSection].title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-white/70 leading-relaxed whitespace-pre-line text-sm">
                  {sections[activeSection].content}
                </div>
              </CardContent>
            </Card>

            {/* Navigation arrows */}
            <div className="flex justify-between mt-6">
              <Button
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                variant="ghost"
                className="text-white/40 hover:text-amber-400"
                disabled={activeSection === 0}
              >
                <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" /> Previous
              </Button>
              <span className="text-white/20 text-sm">
                {activeSection + 1} / {sections.length}
              </span>
              <Button
                onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                variant="ghost"
                className="text-white/40 hover:text-amber-400"
                disabled={activeSection === sections.length - 1}
              >
                Next <ArrowIcon size={16} color="#9CA3AF" direction="right" className="ml-2" />
              </Button>
            </div>
          </div>

          {/* LORE Library Link */}
          <div className="mt-8 p-4 rounded-lg bg-purple-900/20 border border-purple-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <WisdomIcon size={20} color="#A855F7" />
              </div>
              <div className="flex-1">
                <h4 className="text-purple-300 font-medium text-sm">Explore the LORE Library</h4>
                <p className="text-white/50 text-xs">Deep wisdom, teachings, and sacred knowledge</p>
              </div>
              <a 
                href="https://belongingsessions.com/lore" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-300 text-sm hover:bg-purple-500/30 transition-colors"
              >
                Visit →
              </a>
            </div>
          </div>

          {/* Belonging E-Book Link */}
          <div className="mt-4 p-4 rounded-lg bg-blue-900/20 border border-blue-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <BookIcon size={20} color="#3B82F6" />
              </div>
              <div className="flex-1">
                <h4 className="text-blue-300 font-medium text-sm">The Belonging E-Book</h4>
                <p className="text-white/50 text-xs">Your guide to compassionate connection</p>
              </div>
              <a 
                href="https://belongingsessions.com/belonging-ebook" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-300 text-sm hover:bg-blue-500/30 transition-colors"
              >
                Get It →
              </a>
            </div>
            
            {/* M.ai.ry Wisdom Bot */}
            <div className="flex items-center gap-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <span className="text-2xl">✨</span>
              </div>
              <div className="flex-1">
                <h4 className="text-purple-300 font-medium text-sm">M.ai.ry - Wisdom Bot</h4>
                <p className="text-white/50 text-xs">AI wisdom based on Radical Belonging</p>
              </div>
              <a 
                href="https://mairy.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-300 text-sm hover:bg-purple-500/30 transition-colors"
              >
                Chat →
              </a>
            </div>
          </div>

          {/* Return button */}
          <div className="text-center mt-12">
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-medium px-8"
            >
              <SparkleIcon size={16} color="#FBBF24" className="mr-2" />
              Return to the ElchmE
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.1); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

// User Selector Component
const UserSelector = ({ selectedUsers, onSelect, onRemove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations`);
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.filter(u => u.first_name || u.email).map(u => ({
          id: u.id,
          name: u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.email,
          email: u.email
        })));
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    setLoading(false);
  };

  const filteredUsers = availableUsers.filter(u => 
    !selectedUsers.find(s => s.id === u.id) &&
    (u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-3">
      {/* Selected Users */}
      <div className="flex flex-wrap gap-2">
        {selectedUsers.map((user, idx) => (
          <Badge 
            key={user.id || idx}
            variant="outline" 
            className="border-amber-500/40 text-amber-300 cursor-pointer hover:bg-red-500/20 flex items-center gap-1 py-1.5"
            onClick={() => onRemove(user)}
          >
            <ProfileIcon size={12} color="#9CA3AF" />
            {user.name}
            <CloseIcon size={12} color="#9CA3AF" className=" ml-1" />
          </Badge>
        ))}
      </div>

      {/* Add User Button */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="border-white/20 text-white/60 hover:border-amber-500/40">
            <UserPlusIcon size={16} color="#8B5CF6" className="mr-2" />
            Add Family Member
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-md z-[100]">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Add Participant</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Search */}
            <div className="relative">
              <SearchIcon size={16} color="rgba(255,255,255,0.4)" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-black/30 border-stone-700 text-white pl-10"
              />
            </div>

            {/* User List */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {loading ? (
                <div className="text-center py-8 text-white/40">Loading...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-white/40 text-sm">No users found</p>
                  <p className="text-white/20 text-xs mt-2">Add manually below</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => {
                      onSelect(user);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className="w-full p-3 bg-black/30 hover:bg-amber-500/10 border border-stone-700 hover:border-amber-500/30 rounded-lg text-left transition-all"
                  >
                    <p className="text-white font-medium text-sm">{user.name}</p>
                    <p className="text-white/40 text-xs">{user.email}</p>
                  </button>
                ))
              )}
            </div>

            {/* Manual Entry */}
            <div className="pt-4 border-t border-stone-700">
              <p className="text-white/40 text-xs mb-2">Or add someone not registered:</p>
              <form onSubmit={(e) => {
                e.preventDefault();
                const name = e.target.manualName.value.trim();
                if (name) {
                  onSelect({ id: `manual-${Date.now()}`, name, email: '' });
                  e.target.reset();
                  setIsOpen(false);
                }
              }} className="flex gap-2">
                <Input
                  name="manualName"
                  placeholder="Enter name"
                  className="bg-black/30 border-stone-700 text-white flex-1"
                />
                <Button type="submit" className="bg-amber-600 hover:bg-amber-500 text-black">
                  Add
                </Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Enhanced Fractal Orb with Grant Math Psychedelic Lava Lamp Visualization
const FractalOrb = ({ onComplete, participants }) => {
  const [phase, setPhase] = useState('intro');
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [breathCount, setBreathCount] = useState(0);
  const [breathProgress, setBreathProgress] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(true);
  const [showSkip, setShowSkip] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowSkip(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Breathing cycle with progress tracking
  useEffect(() => {
    if (phase !== 'breathing') return;
    
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 0.025;
      setBreathProgress(Math.min(progress, 1));
    }, 100);
    
    const breathCycle = () => {
      setBreathProgress(0);
      setBreathPhase(prev => {
        if (prev === 'inhale') return 'hold';
        if (prev === 'hold') return 'exhale';
        setBreathCount(c => c + 1);
        return 'inhale';
      });
    };
    const interval = setInterval(breathCycle, 4000);
    
    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'intro') setTimeout(() => setPhase('breathing'), 4000);
    if (breathCount >= 3) setPhase('ready');
  }, [phase, breathCount]);

  // Ethereal Audio
  useEffect(() => {
    if (!audioPlaying) return;
    
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    const createTone = (freq, detune, gain) => {
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      const filter = audioCtx.createBiquadFilter();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
      osc.detune.setValueAtTime(detune, audioCtx.currentTime);
      
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(600, audioCtx.currentTime);
      
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(gain, audioCtx.currentTime + 3);
      
      osc.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      osc.start();
      
      const modulate = () => {
        const now = audioCtx.currentTime;
        osc.frequency.setValueAtTime(freq + Math.sin(now * 0.3) * 15, now);
      };
      const modInterval = setInterval(modulate, 100);
      
      return { osc, gainNode, cleanup: () => {
        clearInterval(modInterval);
        gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 1);
        setTimeout(() => osc.stop(), 1000);
      }};
    };

    const tones = [
      createTone(220, 0, 0.06),
      createTone(277.18, 7, 0.04),
      createTone(329.63, -5, 0.03),
      createTone(440, 12, 0.025),
    ];

    audioRef.current = { audioCtx, tones };

    return () => {
      tones.forEach(t => t.cleanup());
      audioCtx.close();
    };
  }, [audioPlaying]);

  // Calculate spiral size based on viewport
  const spiralSize = Math.min(window.innerWidth, window.innerHeight) * 0.85;

  return (
    <div className="fixed inset-0 bg-[#080A0E] z-50 flex items-center justify-center overflow-hidden" data-testid="fractal-orb-meditation">
      {/* Cosmic Art Installation - Layered images with Grant Spiral */}
      <CosmicArtInstallation 
        mode="full"
        intensity={phase === 'breathing' ? 1.2 : 1}
        imageOpacity={0.35}
        transitionDuration={10000}
        breathSync={phase === 'breathing' ? { phase: breathPhase, progress: breathProgress } : null}
        showSpiral={true}
      />
      
      {/* Overlay gradient for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40 pointer-events-none" />
      
      <div className="relative z-10 text-center px-8 max-w-2xl">
        {phase === 'intro' && (
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-light text-white/90 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Enter the Field
            </h1>
            <p className="text-lg text-white/60 leading-relaxed">
              Before we begin, we create a space of mutual receptivity...
            </p>
          </div>
        )}
        
        {phase === 'breathing' && (
          <div className="animate-fade-in">
            <div className="mb-8">
              <div className={`w-32 h-32 mx-auto rounded-full border-2 transition-all duration-[4000ms] backdrop-blur-sm ${
                breathPhase === 'inhale' ? 'scale-125 border-amber-400/60 bg-amber-500/10' :
                breathPhase === 'hold' ? 'scale-125 border-amber-500/80 bg-amber-500/15' :
                'scale-100 border-amber-300/40 bg-amber-500/5'
              }`}>
                <div className="w-full h-full rounded-full flex items-center justify-center">
                  <span className="text-2xl text-amber-300/90 font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {breathPhase === 'inhale' ? 'Breathe In' : breathPhase === 'hold' ? 'Hold' : 'Release'}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-white/50 text-sm mb-2">Breath {breathCount + 1} of 3</p>
            <p className="text-white/40 text-xs max-w-md mx-auto leading-relaxed mt-6">
              {participants && participants.length > 0 
                ? `Together with ${participants.map(p => p.name || p).join(' & ')}, entering a state of love...`
                : 'Release the grip of thoughts, emotions, history... Just be present.'
              }
            </p>
          </div>
        )}
        
        {phase === 'ready' && (
          <div className="animate-fade-in">
            <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/20 border border-amber-500/40 flex items-center justify-center backdrop-blur-sm">
              <StarIcon size={40} color="#FBBF24" />
            </div>
            <h2 className="text-3xl font-light text-white/90 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Field is Open
            </h2>
            <p className="text-white/50 text-sm mb-8 max-w-md mx-auto leading-relaxed">
              From this state of relaxed potency, we can see clearly, feel deeply, 
              and discover the golden thread that connects us all.
            </p>
            <Button
              onClick={onComplete}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-medium px-8 py-6 text-lg"
              data-testid="begin-alchemy-btn"
            >
              <PlayIcon size={20} color="#F59E0B" className="mr-2" /> Begin the ElchmE
            </Button>
          </div>
        )}
      </div>
      
      <button
        onClick={() => setAudioPlaying(!audioPlaying)}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm"
      >
        {audioPlaying ? <VolumeIcon size={20} color="rgba(255,255,255,0.6)" /> : <VolumeIcon size={20} color="rgba(255,255,255,0.4)" muted={true} />}
      </button>
      
      {showSkip && phase !== 'ready' && (
        <button onClick={onComplete} className="absolute bottom-8 right-8 text-white/60 hover:text-white text-sm transition-colors font-medium z-50" data-testid="skip-meditation-btn">
          Skip Intro →
        </button>
      )}
      
      <div className="absolute bottom-8 left-8 max-w-xs text-left">
        <p className="text-[10px] text-white/20 leading-relaxed">
          "Start with a vast embrace of love for the fragile parts of you."
        </p>
      </div>
    </div>
  );
};

// Multi-Person Input Component
const PersonInput = ({ person, color, value, onChange, placeholder, label, onFocus, isActive }) => {
  const colorStyles = {
    blue: 'bg-blue-900/20 border-blue-500/30 focus-within:border-blue-400',
    red: 'bg-red-900/20 border-red-500/30 focus-within:border-red-400',
    green: 'bg-emerald-900/20 border-emerald-500/30 focus-within:border-emerald-400',
  };
  const textColors = {
    blue: 'text-blue-300',
    red: 'text-red-300',
    green: 'text-emerald-300',
  };
  const iconColors = {
    blue: '#93C5FD',
    red: '#FCA5A5',
    green: '#6EE7B7',
  };

  return (
    <div 
      className={`rounded-lg p-3 border transition-all ${colorStyles[color]} ${isActive ? 'ring-2 ring-amber-500/50 shadow-lg shadow-amber-500/10' : ''}`}
      onClick={onFocus}
    >
      <div className="flex items-center gap-2 mb-2">
        <ProfileIcon size={12} color={iconColors[color]} />
        <span className={`text-xs font-medium ${textColors[color]}`}>
          {person.name || person}
        </span>
        {label && <span className="text-white/30 text-[10px]">· {label}</span>}
        {isActive && <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded ml-auto border border-amber-500/30">Active</span>}
      </div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={placeholder}
        className="bg-transparent border-0 text-white placeholder:text-white/20 min-h-[60px] p-0 focus:ring-0 resize-none text-sm"
      />
    </div>
  );
};

// Golden Orb Background Component - Using Cosmic Art Installation for trippy psychedelic effect
const GoldOrbBackground = ({ intensity = 0, formState = null }) => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Cosmic Art Installation with contemplation images */}
      <CosmicArtInstallation 
        mode="background"
        intensity={0.4 + intensity * 0.4}
        imageOpacity={0.25 + intensity * 0.15}
        transitionDuration={15000}
        showSpiral={true}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
    </div>
  );
};

// Complete Participant Summary Component for Gold Step
const ParticipantJourneySummary = ({ formState, hasParticipants }) => {
  if (!formState) return null;

  return (
    <div className="bg-gradient-to-b from-white/5 to-white/[0.02] rounded-xl p-4 border border-white/10 backdrop-blur-sm" data-testid="participant-journey-summary">
      <h4 className="text-amber-400 text-sm font-medium mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        <SparkleIcon size={16} color="#FBBF24" />
        Complete Journey Summary
      </h4>
      
      {hasParticipants ? (
        <div className="space-y-4">
          {formState.participants.map((person, idx) => (
            <div key={person.id} className="pb-4 border-b border-white/5 last:border-0 last:pb-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500/30 to-amber-600/20 border border-amber-500/40 flex items-center justify-center">
                  <ProfileIcon size={16} color="#FBBF24" />
                </div>
                <span className="text-amber-300 font-medium text-sm">{person.name}</span>
              </div>
              
              <div className="space-y-2 pl-10">
                {/* Observe (Blue) */}
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ObserveIcon size={12} color="#60A5FA" />
                  </div>
                  <div>
                    <span className="text-blue-400 text-[10px] uppercase tracking-wider block mb-0.5">Observes</span>
                    <p className="text-white/80 text-xs leading-relaxed">
                      {formState.participantInputs[person.id]?.blue || <span className="text-white/30 italic">No observation recorded</span>}
                    </p>
                  </div>
                </div>
                
                {/* Feel (Red) */}
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FeelIcon size={12} color="#F87171" />
                  </div>
                  <div>
                    <span className="text-red-400 text-[10px] uppercase tracking-wider block mb-0.5">Feels</span>
                    <p className="text-white/80 text-xs leading-relaxed">
                      {formState.participantInputs[person.id]?.red || <span className="text-white/30 italic">No feelings recorded</span>}
                    </p>
                  </div>
                </div>
                
                {/* Need (Green) */}
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <NeedIcon size={12} color="#34D399" />
                  </div>
                  <div>
                    <span className="text-emerald-400 text-[10px] uppercase tracking-wider block mb-0.5">Needs</span>
                    <p className="text-white/80 text-xs leading-relaxed">
                      {formState.participantInputs[person.id]?.green || <span className="text-white/30 italic">No needs recorded</span>}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Self Journey */}
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ObserveIcon size={12} color="#60A5FA" />
            </div>
            <div>
              <span className="text-blue-400 text-[10px] uppercase tracking-wider block mb-0.5">I Observe</span>
              <p className="text-white/80 text-xs leading-relaxed">
                {formState.participantInputs['self']?.blue || <span className="text-white/30 italic">No observation</span>}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FeelIcon size={12} color="#F87171" />
            </div>
            <div>
              <span className="text-red-400 text-[10px] uppercase tracking-wider block mb-0.5">I Feel</span>
              <p className="text-white/80 text-xs leading-relaxed">
                {formState.participantInputs['self']?.red || <span className="text-white/30 italic">No feelings</span>}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <NeedIcon size={12} color="#34D399" />
            </div>
            <div>
              <span className="text-emerald-400 text-[10px] uppercase tracking-wider block mb-0.5">I Need</span>
              <p className="text-white/80 text-xs leading-relaxed">
                {formState.participantInputs['self']?.green || <span className="text-white/30 italic">No needs</span>}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ElchmE() {
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showPurposeIntro, setShowPurposeIntro] = useState(false);
  const [showChapterIntro, setShowChapterIntro] = useState(false);
  const [showVisionQuest, setShowVisionQuest] = useState(false);
  const [showOFNRHelper, setShowOFNRHelper] = useState(false);
  const [showObjectivityReminder, setShowObjectivityReminder] = useState(true);
  const [showBeforeWeBegin, setShowBeforeWeBegin] = useState(false);
  const [showVisionVideo, setShowVisionVideo] = useState(false);
  const [showGoldenSummary, setShowGoldenSummary] = useState(false);
  const [showPracticeCards, setShowPracticeCards] = useState(false);
  
  // Soul Score state
  const [showSoulScore, setShowSoulScore] = useState(false);
  const [soulScoreCheckpoint, setSoulScoreCheckpoint] = useState('timeZero');
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [hasCompletedSoulScore, setHasCompletedSoulScore] = useState({
    timeZero: false,
    belonging: false,
    vision: false,
    story: false
  });
  
  // Experience mode: 'conflict', 'appreciation', 'romance', or 'practice'
  const [experienceMode, setExperienceMode] = useState('conflict');
  
  // Mindful SOS states
  const [showTimeoutSOS, setShowTimeoutSOS] = useState(false);
  const [showStayPresent, setShowStayPresent] = useState(false);
  const [showRolePlay, setShowRolePlay] = useState(false);
  const [showInstantSummary, setShowInstantSummary] = useState(false);
  const [showMeditation, setShowMeditation] = useState(false);
  const [showPrimer, setShowPrimer] = useState(false);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [loreInput, setLoreInput] = useState('');
  const [showLoreForm, setShowLoreForm] = useState(false);
  const [activeParticipantId, setActiveParticipantId] = useState(null);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const [showGrayWordsPreflection, setShowGrayWordsPreflection] = useState(false);
  const [hasSeenGrayWordsIntro, setHasSeenGrayWordsIntro] = useState(false);

  // Ref to track latest activeParticipantId for chip selection (fixes stale closure bug)
  const activeParticipantIdRef = useRef(activeParticipantId);
  useEffect(() => {
    activeParticipantIdRef.current = activeParticipantId;
  }, [activeParticipantId]);

  // NVC Wisdom Manager - shows periodic wisdom popups
  const { currentWisdom, dismissWisdom } = useWisdomManager(currentStep, view === 'new-project');

  // Honesty/Empathy Dance mode
  const [danceMode, setDanceMode] = useState('selfEmpathy');
  
  // Soul Reminder - periodic reminder to stay connected to soul
  const { showReminder: showSoulReminder, dismissReminder: dismissSoulReminder, triggerReminder: triggerSoulReminder } = useSoulReminder(currentStep, view === 'new-project');
  
  // Needs Song Generator dialog
  const [showNeedsSong, setShowNeedsSong] = useState(false);
  const [showIChooseTo, setShowIChooseTo] = useState(false);
  const [userBelongingNeeds, setUserBelongingNeeds] = useState([]);
  
  // Feelings/Needs Helper popup
  const [showFeelingsHelper, setShowFeelingsHelper] = useState(false);
  
  // Completion dialog - offers Chapter 3 (Vision Quest) option
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [completedProjectData, setCompletedProjectData] = useState(null);

  // Multi-person form state
  const [formState, setFormState] = useState({
    title: '',
    participants: [],
    participantInputs: {}, // { participantId: { blue: '', red: '', green: '', empathyGuesses: { targetId: { red: '', green: '' } } } }
    gold: '',
    owner: '',
    checkInDate: ''
  });
  
  // Empathy mode toggle - when true, user is guessing what others feel/need
  const [empathyMode, setEmpathyMode] = useState(false);
  const [empathyTargetId, setEmpathyTargetId] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('elchme_projects');
    if (saved) setProjects(JSON.parse(saved));
  }, []);
  
  // Auto-dismiss objectivity reminder after 7 seconds on Red step
  useEffect(() => {
    if (currentStep === 2 && !empathyMode) {
      setShowObjectivityReminder(true);
      const timer = setTimeout(() => setShowObjectivityReminder(false), 7000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, empathyMode]);

  const saveProjects = useCallback((newProjects) => {
    setProjects(newProjects);
    localStorage.setItem('elchme_projects', JSON.stringify(newProjects));
  }, []);

  const shareURL = () => {
    const url = window.location.origin + '/elchme';
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied! Share with your family.');
    setTimeout(() => setCopied(false), 2000);
  };

  const addParticipant = (user) => {
    if (!formState.participants.find(p => p.id === user.id)) {
      setFormState(prev => ({
        ...prev,
        participants: [...prev.participants, user],
        participantInputs: {
          ...prev.participantInputs,
          [user.id]: { blue: '', red: '', green: '' }
        }
      }));
      // Set new participant as active
      setActiveParticipantId(user.id);
    }
  };

  const removeParticipant = (user) => {
    setFormState(prev => {
      const newInputs = { ...prev.participantInputs };
      delete newInputs[user.id];
      return {
        ...prev,
        participants: prev.participants.filter(p => p.id !== user.id),
        participantInputs: newInputs
      };
    });
    if (activeParticipantId === user.id) {
      setActiveParticipantId(null);
    }
  };

  // Past-tense words that might indicate dwelling on the past
  const PAST_TENSE_PATTERNS = /\b(was|were|had|didn't|wasn't|weren't|hadn't|should have|could have|would have|used to|always|never|back then|remember when|years ago)\b/i;
  
  // Track if we've shown reminders recently to avoid spam
  const [lastStayPresentShown, setLastStayPresentShown] = useState(0);
  const [lastRolePlayShown, setLastRolePlayShown] = useState(0);
  
  const updateParticipantInput = (participantId, field, value) => {
    setFormState(prev => ({
      ...prev,
      participantInputs: {
        ...prev.participantInputs,
        [participantId]: {
          ...prev.participantInputs[participantId],
          [field]: value
        }
      }
    }));
    setActiveParticipantId(participantId);
    
    // Strategic popup triggers
    const now = Date.now();
    
    // 1. Stay Present trigger - when typing past-tense in observations (blue)
    if (field === 'blue' && PAST_TENSE_PATTERNS.test(value)) {
      // Only show if not shown in last 60 seconds
      if (now - lastStayPresentShown > 60000) {
        setTimeout(() => {
          setShowStayPresent(true);
          setLastStayPresentShown(Date.now());
        }, 1500); // Gentle delay so it doesn't interrupt typing
      }
    }
    
    // 2. Role-Play suggestion - during feelings step (red) when expressing strong emotions
    const STRONG_EMOTION_PATTERNS = /\b(angry|furious|hurt|betrayed|frustrated|annoyed|upset|devastated|heartbroken|resentful)\b/i;
    if (field === 'red' && STRONG_EMOTION_PATTERNS.test(value) && formState.participants.length > 0) {
      // Only show if not shown in last 90 seconds and there are other participants
      if (now - lastRolePlayShown > 90000) {
        setTimeout(() => {
          setShowRolePlay(true);
          setLastRolePlayShown(Date.now());
        }, 2000);
      }
    }
  };
  
  // Update empathy guess - what I think another person feels/needs
  const updateEmpathyGuess = (fromId, targetId, field, value) => {
    setFormState(prev => ({
      ...prev,
      participantInputs: {
        ...prev.participantInputs,
        [fromId]: {
          ...prev.participantInputs[fromId],
          empathyGuesses: {
            ...(prev.participantInputs[fromId]?.empathyGuesses || {}),
            [targetId]: {
              ...(prev.participantInputs[fromId]?.empathyGuesses?.[targetId] || {}),
              [field]: value
            }
          }
        }
      }
    }));
  };
  
  // Get other participants (excluding the active one)
  const getOtherParticipants = (excludeId) => {
    return formState.participants.filter(p => p.id !== excludeId);
  };

  // Start with disclaimer (if needed), then purpose intro, then meditation
  const startNewProject = () => {
    // Check for secret skip flag
    const skipIntro = localStorage.getItem('elchme_skip_intro');
    
    if (skipIntro === 'true') {
      // Even when skipping, show the mandatory BeforeWeBegin intro
      localStorage.removeItem('elchme_skip_intro');
      setShowBeforeWeBegin(true);
      toast.success('⚡ Skipping to BeforeWeBegin...', { duration: 1500 });
      return;
    }
    
    // Normal flow: Show Chapter 1: Belonging intro first
    setShowChapterIntro(true);
  };
  
  const onBeforeWeBeginComplete = () => {
    setShowBeforeWeBegin(false);
    
    // Check if this was a skip (no disclaimer shown yet)
    const wasSkipped = localStorage.getItem('elchme_skip_used');
    
    if (wasSkipped === 'true') {
      // Skip flow: go straight to form
      localStorage.removeItem('elchme_skip_used');
      setView('new-project');
      setCurrentStep(0);
    } else {
      // Normal flow: continue with disclaimer or purpose
      if (!hasAcceptedDisclaimer()) {
        setShowDisclaimer(true);
      } else {
        setShowPurposeIntro(true);
      }
    }
  };

  const onChapterIntroComplete = () => {
    setShowChapterIntro(false);
    // Always show BeforeWeBegin (mandatory intro)
    setShowBeforeWeBegin(true);
  };

  const onDisclaimerAccept = () => {
    acceptDisclaimer();
    setShowDisclaimer(false);
    setShowPurposeIntro(true);
  };
  
  // Launch Vision Quest (Chapter 2) after completing ElchmE
  const launchVisionQuest = () => {
    const userName = localStorage.getItem('elchme_user_name') || 'Friend';
    const confirmMessage = `${userName}, you're about to begin your Vision Quest.\n\nThis journey will help you define your core values, discover your purpose, and create your Big Hairy Audacious Goal.\n\nReady to continue?`;
    
    if (window.confirm(confirmMessage)) {
      // Show video intro first
      setShowVisionVideo(true);
    }
  };
  
  const onVisionVideoComplete = () => {
    setShowVisionVideo(false);
    setShowVisionQuest(true);
  };
  
  const onVisionVideoSkip = () => {
    setShowVisionVideo(false);
    setShowVisionQuest(true);
    toast.info('Spiral intro skipped', { duration: 1500 });
  };

  const onDisclaimerDecline = () => {
    setShowDisclaimer(false);
    toast.info('No problem! Come back whenever you\'re ready.');
  };

  const onPurposeIntroComplete = () => {
    setShowPurposeIntro(false);
    setShowMeditation(true);
  };

  const onMeditationComplete = () => {
    setShowMeditation(false);
    // Show Soul Score at start of session (timeZero checkpoint)
    if (!hasCompletedSoulScore.timeZero) {
      setSoulScoreCheckpoint('timeZero');
      setShowSoulScore(true);
    } else {
      setView('new-project');
    }
  };
  
  // Handle Soul Score completion
  const handleSoulScoreComplete = (result) => {
    setShowSoulScore(false);
    setHasCompletedSoulScore(prev => ({
      ...prev,
      [result.checkpoint]: true
    }));
    
    // Navigate based on which checkpoint was completed
    if (result.checkpoint === 'timeZero') {
      setView('new-project');
    } else if (result.checkpoint === 'belonging') {
      // Show completion dialog after belonging
      setShowCompletionDialog(true);
    }
  };
  
  // Handle Soul Score skip
  const handleSoulScoreSkip = () => {
    setShowSoulScore(false);
    // Navigate based on current checkpoint
    if (soulScoreCheckpoint === 'timeZero') {
      setView('new-project');
    } else if (soulScoreCheckpoint === 'belonging') {
      setShowCompletionDialog(true);
    }
  };

  const createProject = () => {
    if (!formState.title || !formState.gold) {
      toast.error('Complete the Gold step first');
      return;
    }

    const newProject = {
      id: Date.now().toString(),
      title: formState.title,
      participants: formState.participants,
      participantInputs: formState.participantInputs,
      gold: formState.gold,
      owner: formState.owner || 'Family',
      checkInDate: formState.checkInDate || new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0],
      status: 'now',
      createdAt: new Date().toISOString(),
      messages: [],
      lore: null
    };

    // Save project
    saveProjects([...projects, newProject]);
    setCompletedProjectData(newProject);
    
    // Show Soul Score for belonging checkpoint before completion dialog
    if (!hasCompletedSoulScore.belonging) {
      setSoulScoreCheckpoint('belonging');
      setShowSoulScore(true);
    } else {
      setShowCompletionDialog(true);
    }
  };
  
  // Handle completion dialog choices
  const handleCompletionChoice = (choice) => {
    setShowCompletionDialog(false);
    
    if (choice === 'vision') {
      // Launch Vision Quest with green needs pre-populated
      setShowVisionQuest(true);
    } else {
      // Just go to board
      toast.success('Gold discovered! Your project is saved.');
      setFormState({ title: '', participants: [], participantInputs: {}, gold: '', owner: '', checkInDate: '' });
      setCurrentStep(0);
      setView('board');
    }
  };
  
  // Extract green needs from the completed conflict resolution for Vision Quest
  const getGreenNeedsForVisionQuest = () => {
    const greenNeeds = [];
    const inputs = formState.participantInputs || {};
    
    // Collect green (needs) from all participants
    Object.values(inputs).forEach(input => {
      if (input?.green) {
        // Parse needs from the green input text
        const needsText = input.green.toLowerCase();
        // Map common need words to Vision Quest values
        const needMappings = {
          'connection': 'Belonging',
          'belonging': 'Belonging',
          'love': 'Love',
          'trust': 'Trust',
          'safety': 'Safety',
          'respect': 'Acceptance',
          'acceptance': 'Acceptance',
          'understanding': 'Empathy',
          'empathy': 'Empathy',
          'peace': 'Peace',
          'harmony': 'Harmony',
          'freedom': 'Freedom',
          'autonomy': 'Freedom',
          'choice': 'Choice',
          'growth': 'Growth',
          'learning': 'Learning',
          'purpose': 'Purpose',
          'meaning': 'Purpose',
          'contribution': 'Contribution',
          'creativity': 'Creativity',
          'expression': 'Expression',
          'authenticity': 'Authenticity',
          'integrity': 'Integrity',
          'joy': 'Joy',
          'fun': 'Fun',
          'play': 'Fun',
          'rest': 'Rest',
          'health': 'Health',
          'support': 'Community',
          'community': 'Community',
          'companionship': 'Companionship',
          'intimacy': 'Intimacy',
          'appreciation': 'Acceptance',
          'recognition': 'Acceptance',
          'consideration': 'Empathy',
          'care': 'Love',
          'compassion': 'Empathy',
          'honesty': 'Integrity',
          'clarity': 'Peace',
          'order': 'Order',
          'beauty': 'Beauty',
          'celebration': 'Celebration',
          'adventure': 'Adventure',
        };
        
        Object.entries(needMappings).forEach(([keyword, value]) => {
          if (needsText.includes(keyword) && !greenNeeds.includes(value)) {
            greenNeeds.push(value);
          }
        });
      }
    });
    
    return greenNeeds;
  };

  // Navigation handler with Gray Words awareness
  const handleNextStep = () => {
    // When moving from Observe (1) to Feel (2), show Gray Words preflection
    if (currentStep === 1 && !hasSeenGrayWordsIntro) {
      setShowGrayWordsPreflection(true);
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  // Called when user proceeds from Gray Words preflection
  const handleGrayWordsComplete = () => {
    setShowGrayWordsPreflection(false);
    setHasSeenGrayWordsIntro(true);
    setCurrentStep(currentStep + 1);
    toast.success("Remember: genuine curiosity is the key to connection", { duration: 3000 });
  };

  const updateProjectStatus = (projectId, newStatus) => {
    const updated = projects.map(p => p.id === projectId ? { ...p, status: newStatus } : p);
    saveProjects(updated);
    setCurrentProject(prev => prev ? { ...prev, status: newStatus } : null);
    if (newStatus === 'done') toast.success('Gold achieved');
  };

  const deleteProject = (projectId) => {
    if (window.confirm('Release this journey?')) {
      saveProjects(projects.filter(p => p.id !== projectId));
      setCurrentProject(null);
      setView('board');
    }
  };

  const addMessage = (projectId, message) => {
    if (!message.trim()) return;
    const updated = projects.map(p => {
      if (p.id === projectId) {
        return { ...p, messages: [...(p.messages || []), {
          id: Date.now().toString(),
          text: message,
          author: 'You',
          timestamp: new Date().toISOString()
        }]};
      }
      return p;
    });
    saveProjects(updated);
    setCurrentProject(prev => prev ? { ...prev, messages: updated.find(p => p.id === projectId)?.messages || [] } : null);
    setNewMessage('');
  };

  const addLore = (projectId, loreText) => {
    if (!loreText.trim()) return;
    const updated = projects.map(p => p.id === projectId ? { ...p, lore: { text: loreText, createdAt: new Date().toISOString() } } : p);
    saveProjects(updated);
    setCurrentProject(prev => prev ? { ...prev, lore: { text: loreText, createdAt: new Date().toISOString() } } : null);
    setLoreInput('');
    setShowLoreForm(false);
    toast.success('Wisdom recorded');
  };

  // Start editing a project
  const startEditingProject = () => {
    setEditedProject({ ...currentProject });
    setIsEditingProject(true);
  };

  // Save edited project
  const saveEditedProject = () => {
    if (!editedProject) return;
    const updated = projects.map(p => p.id === editedProject.id ? { ...editedProject, updatedAt: new Date().toISOString() } : p);
    saveProjects(updated);
    setCurrentProject(editedProject);
    setIsEditingProject(false);
    setEditedProject(null);
    toast.success('Project updated');
  };

  // Cancel editing
  const cancelEditingProject = () => {
    setIsEditingProject(false);
    setEditedProject(null);
  };

  // Update edited project field
  const updateEditedField = (field, value) => {
    setEditedProject(prev => ({ ...prev, [field]: value }));
  };

  // Update edited participant input
  const updateEditedParticipantInput = (participantId, field, value) => {
    setEditedProject(prev => ({
      ...prev,
      participantInputs: {
        ...prev.participantInputs,
        [participantId]: {
          ...(prev.participantInputs?.[participantId] || {}),
          [field]: value
        }
      }
    }));
  };

  // Export conflict to SketchHub as a project
  const exportToSketchHub = async (project) => {
    try {
      const userId = localStorage.getItem('elchme_user_id') || localStorage.getItem('workbook_user_id');
      const userName = localStorage.getItem('elchme_user_name') || localStorage.getItem('workbook_user_name') || 'Anonymous';
      
      if (!userId) {
        toast.error('Please log in to export to SketchHub');
        return;
      }

      // Prepare the project data for SketchHub
      const participants = project.participants || [];
      const inputs = project.participantInputs || {};
      
      // Build description from OFNR data
      let description = `**Conflict Resolution from ElchmE**\n\n`;
      description += `**Friction:** ${project.title}\n\n`;
      
      if (participants.length > 0) {
        participants.forEach(person => {
          const personInputs = inputs[person.id] || {};
          description += `**${person.name}:**\n`;
          description += `- Observation: ${personInputs.blue || 'Not captured'}\n`;
          description += `- Feelings: ${personInputs.red || 'Not captured'}\n`;
          description += `- Needs: ${personInputs.green || 'Not captured'}\n\n`;
        });
      } else {
        const selfInputs = inputs['self'] || {};
        description += `- Observation: ${selfInputs.blue || project.blue || 'Not captured'}\n`;
        description += `- Feelings: ${selfInputs.red || project.red || 'Not captured'}\n`;
        description += `- Needs: ${selfInputs.green || project.green || 'Not captured'}\n\n`;
      }
      
      description += `**Gold (Resolution):** ${project.gold || 'In progress'}\n`;

      const sketchHubProject = {
        name: `ElchmE: ${project.title}`,
        description: description,
        status: 'active',
        priority: project.status === 'now' ? 'high' : project.status === 'next' ? 'medium' : 'low',
        color: '#F59E0B', // Amber for ElchmE projects
        owner_id: userId,
        owner_name: userName,
        template: 'conflict_resolution',
        tags: ['elchme', 'conflict', ...participants.map(p => p.name)],
        source: 'elchme',
        source_id: project.id,
        progress: project.status === 'done' ? 100 : 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sketch/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sketchHubProject)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Also export messages if any
        if (project.messages && project.messages.length > 0) {
          for (const msg of project.messages) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sketch/projects/${data.id}/messages`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                content: msg.text,
                author_name: msg.author,
                created_at: msg.timestamp
              })
            });
          }
        }

        toast.success('Exported to SketchHub!', {
          action: {
            label: 'View',
            onClick: () => navigate(`/sketch?project=${data.id}`)
          }
        });
        
        return data;
      } else {
        throw new Error('Failed to create SketchHub project');
      }
    } catch (error) {
      console.error('Export to SketchHub failed:', error);
      toast.error('Failed to export to SketchHub');
    }
  };

  // Show Chapter 1: Belonging intro (cinematic chapter title)
  if (showChapterIntro) {
    return (
      <ChapterTransition 
        chapter="belonging"
        onComplete={onChapterIntroComplete}
        autoAdvance={true}
        showSkip={true}
      />
    );
  }

  // Show BeforeWeBegin mandatory intro (even when skipping)
  if (showBeforeWeBegin) {
    return <BeforeWeBegin onComplete={onBeforeWeBeginComplete} />;
  }
  
  // Show Vision Quest video intro
  if (showVisionVideo) {
    return (
      <VisionQuestVideoIntro
        onComplete={onVisionVideoComplete}
        onSkip={onVisionVideoSkip}
      />
    );
  }

  // Show Vision Quest (Chapter 2) if triggered
  if (showVisionQuest) {
    // Get green needs from conflict resolution to pre-populate values
    const initialValues = getGreenNeedsForVisionQuest();
    
    return (
      <VisionQuest
        userId={localStorage.getItem('elchme_user_id')}
        userName={localStorage.getItem('elchme_user_name')}
        onComplete={(data) => {
          setShowVisionQuest(false);
          setShowCompletionDialog(false);
          toast.success('Vision Quest completed!');
          // Clear form state after Vision Quest
          setFormState({ title: '', participants: [], participantInputs: {}, gold: '', owner: '', checkInDate: '' });
          setCurrentStep(0);
          setView('board');
        }}
        onClose={() => {
          setShowVisionQuest(false);
          // If coming from completion dialog, go to board
          if (completedProjectData) {
            setFormState({ title: '', participants: [], participantInputs: {}, gold: '', owner: '', checkInDate: '' });
            setCurrentStep(0);
            setView('board');
            setCompletedProjectData(null);
          }
        }}
        initialData={initialValues.length > 0 ? { values: initialValues } : null}
        participants={formState.participants}
        isGroup={formState.participants?.length > 1}
      />
    );
  }
  
  // Show Practice Cards (flashcard experience)
  if (showPracticeCards) {
    return (
      <PracticeCards
        userId={localStorage.getItem('elchme_user_id')}
        userName={localStorage.getItem('elchme_user_name')}
        onComplete={() => {
          setShowPracticeCards(false);
          setView('landing');
        }}
        onClose={() => {
          setShowPracticeCards(false);
          setView('landing');
        }}
        onStartBelonging={() => {
          setShowPracticeCards(false);
          setExperienceMode('conflict');
          startNewProject();
        }}
      />
    );
  }

  // Show Purpose Intro first (cinematic explanation of why we're here)
  if (showPurposeIntro) {
    return <PurposeIntro onComplete={onPurposeIntroComplete} onSkip={onPurposeIntroComplete} />;
  }

  // Then show meditation/breathing exercise
  if (showMeditation) {
    return <FractalOrb onComplete={onMeditationComplete} participants={formState.participants} />;
  }

  // LANDING
  const renderLanding = () => (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="fixed inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center">
          <header className="mb-10">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-500/10 to-amber-700/5 border border-amber-500/20 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <RefreshIcon size={28} color="#000000" />
                </div>
              </div>
            </div>
            <h1 className="text-4xl font-light tracking-wide text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="text-blue-400">El</span>
              <span className="text-red-400">ch</span>
              <span className="text-emerald-400">m</span>
              <span className="text-amber-400">E</span>
            </h1>
            <p className="text-sm text-amber-500/80 font-light tracking-widest uppercase">Empathetic Alchemy</p>
          </header>

          <div className="mb-8 px-4 space-y-4">
            <p className="text-white/70 text-base font-medium leading-relaxed">
              ElchmE will now guide you to the reality we all seek. By simply connecting more, we can discover Gold together.
            </p>
            <p className="text-amber-400/60 text-sm leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
              Let us remember, we belong to each other...
            </p>
          </div>

          {/* Color Flow */}
          <div className="flex justify-center items-center gap-1.5 mb-8">
            {[
              { icon: ObserveIcon, color: 'blue', label: 'OBSERVE', hexColor: '#3B82F6', isBespoke: true },
              { icon: FeelIcon, color: 'red', label: 'FEEL', hexColor: '#EF4444', isBespoke: true },
              { icon: NeedIcon, color: 'emerald', label: 'NEED', hexColor: '#10B981', isBespoke: true },
              { icon: GoldIcon, color: 'amber', label: 'GOLD', hexColor: '#F59E0B', isBespoke: true },
            ].map((item, idx) => (
              <React.Fragment key={item.label}>
                {idx > 0 && <ArrowIcon size={8} color="rgba(255,255,255,0.2)" direction="right" />}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full bg-${item.color}-500/15 border border-${item.color}-500/30 flex items-center justify-center mb-1`}>
                    {item.isBespoke ? (
                      <item.icon size={20} color={item.hexColor} />
                    ) : (
                      <item.icon className={`w-4 h-4 text-${item.color}-400`} />
                    )}
                  </div>
                  <span className={`text-[8px] text-${item.color}-400/60`}>{item.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Actions */}
          <div className="space-y-3 px-4">
            {/* Mode Selector - Now with Practice Cards */}
            <div className="grid grid-cols-4 gap-1.5 p-1 bg-zinc-800/50 rounded-xl">
              <button
                onClick={() => setExperienceMode('conflict')}
                data-testid="mode-conflict-btn"
                className={`py-2 px-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                  experienceMode === 'conflict' 
                    ? 'bg-amber-500 text-black' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <FireIcon size={16} color={experienceMode === 'conflict' ? '#000' : '#F59E0B'} />
                Conflict
              </button>
              <button
                onClick={() => setExperienceMode('appreciation')}
                data-testid="mode-appreciation-btn"
                className={`py-2 px-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                  experienceMode === 'appreciation' 
                    ? 'bg-emerald-500 text-black' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <HeartIcon size={16} color={experienceMode === 'appreciation' ? '#000' : '#10B981'} />
                Appreciation
              </button>
              <button
                onClick={() => setExperienceMode('romance')}
                data-testid="mode-romance-btn"
                className={`py-2 px-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 relative ${
                  experienceMode === 'romance' 
                    ? 'bg-rose-500 text-black' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <HeartFilledIcon size={16} color={experienceMode === 'romance' ? '#000' : '#F43F5E'} />
                Romance
                <span className="absolute -top-0.5 -right-0.5 px-0.5 py-0.5 bg-rose-400 text-black text-[5px] font-bold rounded uppercase leading-none">
                  Beta
                </span>
              </button>
              <button
                onClick={() => setExperienceMode('practice')}
                data-testid="mode-practice-btn"
                className={`py-2 px-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                  experienceMode === 'practice' 
                    ? 'bg-purple-500 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <BookIcon size={16} color={experienceMode === 'practice' ? '#FFF' : '#A855F7'} />
                Practice
              </button>
            </div>
            
            <Button 
              onClick={() => experienceMode === 'practice' ? setShowPracticeCards(true) : startNewProject()}
              className={`w-full font-semibold py-6 text-base ${
                experienceMode === 'practice'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white'
                  : experienceMode === 'romance'
                  ? 'bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-400 hover:to-pink-500 text-white'
                  : experienceMode === 'appreciation'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-black'
                  : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black'
              }`}
            >
              {experienceMode === 'practice' ? (
                <>
                  <SparkleIcon size={20} color="#FFFFFF" className="mr-2" /> Practice Cards
                </>
              ) : experienceMode === 'romance' ? (
                <>
                  <HeartFilledIcon size={20} color="#FFFFFF" className="mr-2" /> Deepen Intimacy
                </>
              ) : experienceMode === 'appreciation' ? (
                <>
                  <HeartFilledIcon size={20} color="#10B981" className="mr-2" /> Express Appreciation
                </>
              ) : (
                <>
                  <StarIcon size={20} color="#FBBF24" className="mr-2" /> Initiate ElchmE
                </>
              )}
            </Button>
            
            {/* Secret Skip Intro Icon */}
            <div className="text-center mt-2">
              <button
                onClick={() => {
                  // Set skip flags
                  localStorage.setItem('elchme_skip_intro', 'true');
                  localStorage.setItem('elchme_skip_used', 'true');
                  localStorage.setItem('elchme_disclaimer_accepted', 'true');
                  // Start project directly
                  startNewProject();
                }}
                className="text-white/10 hover:text-amber-400/40 transition-colors p-1"
                title="Skip intro (secret)"
              >
                <ChevronIcon size={8} direction="down" color="currentColor" />
              </button>
            </div>
            
            {projects.length > 0 && (
              <Button onClick={() => setView('board')} variant="outline" className="w-full border-amber-500/30 text-amber-300 hover:bg-amber-500/10 py-5">
                View Journeys · {projects.length}
              </Button>
            )}

            <Button 
              onClick={() => setShowPrimer(true)}
              variant="ghost" 
              className="w-full text-white/40 hover:text-amber-400 hover:bg-amber-500/10"
            >
              <InfoIcon size={16} color="#3B82F6" className="mr-2" /> Learn the Way
            </Button>

            <div className="flex gap-2 pt-2">
              <Button onClick={() => navigate('/sketch-hub')} variant="ghost" className="flex-1 text-white/30 hover:text-amber-400 text-xs py-4">
                <PenIcon size={12} color="#F59E0B" className="mr-1" /> Sketch
              </Button>
              <Button onClick={() => setView('lore')} variant="ghost" className="flex-1 text-white/30 hover:text-amber-400 text-xs py-4">
                <WisdomIcon size={12} color="#F472B6" className="mr-1" /> Wisdom
              </Button>
              <Button onClick={shareURL} variant="ghost" className="flex-1 text-white/30 hover:text-amber-400 text-xs py-4">
                {copied ? <CheckIcon size={12} color="#10B981" className="mr-1" /> : <ShareIcon size={12} color="#06B6D4" className="mr-1" />} Share
              </Button>
            </div>
            
            {/* LORE Library Link */}
            <a 
              href="https://belongingsessions.com/lore" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-xs text-purple-400/70 hover:text-purple-400 transition-colors"
            >
              <WisdomIcon size={12} color="#A855F7" /> Explore the LORE Library →
            </a>
            
            {/* Practice Cards Quick Access */}
            <button 
              onClick={() => {
                setExperienceMode('practice');
                setShowPracticeCards(true);
              }}
              className="flex items-center justify-center gap-2 w-full py-2 text-xs text-indigo-400/70 hover:text-indigo-400 transition-colors"
            >
              <SparkleIcon size={12} color="#818CF8" /> Practice Cards - NVC Flashcards →
            </button>
            
            {/* Belonging E-Book Link */}
            <a 
              href="https://belongingsessions.com/belonging-ebook" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-xs text-blue-400/70 hover:text-blue-400 transition-colors"
            >
              <BookIcon size={12} color="#3B82F6" /> Get the Belonging E-Book →
            </a>
            
            {/* Secret History Game Link */}
            <a 
              href="https://playsecrethistory.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-xs text-emerald-400/70 hover:text-emerald-400 transition-colors"
            >
              <CompassIcon size={12} color="#10B981" /> Secret History of Humankind Game →
            </a>
            
            {/* M.ai.ry Wisdom Bot Link */}
            <a 
              href="https://mairy.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-xs text-purple-400/70 hover:text-purple-400 transition-colors"
            >
              <span className="text-purple-400">✨</span> M.ai.ry - Wisdom Bot →
            </a>
            
            {/* Empathy SOS Button */}
            <div className="pt-4 border-t border-white/5 mt-2">
              <EmpathySOS 
                userId={localStorage.getItem('elchme_user_id')}
                userName={localStorage.getItem('elchme_user_name')}
                triggerVariant="footer"
                className="w-full justify-center"
              />
            </div>

            <Button onClick={() => navigate('/')} variant="ghost" className="w-full text-white/20 hover:text-white/40 text-xs">
              <HomeIcon size={12} color="#F59E0B" className="mr-1" /> Home
            </Button>
            
            {/* Belonging Sessions Dashboard Link */}
            <a 
              href="https://belongingsessions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full mt-2"
            >
              <Button variant="outline" className="w-full text-amber-400/70 border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-500/50 text-xs">
                <UsersIcon size={12} color="#FBBF24" className="mr-2" /> Return to Belonging Sessions Dashboard
              </Button>
            </a>
          </div>

          <div className="mt-10 pt-6 border-t border-white/5 space-y-3">
            <p className="text-[9px] text-white/15 tracking-widest">BELONGING SESSIONS · DEFIANCE SPRINGS</p>
            <div className="flex justify-center">
              <Credits triggerClassName="text-[10px] text-white/20 hover:text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      <PhilosophyPrimer isOpen={showPrimer} onClose={() => setShowPrimer(false)} />
      
      {/* Liability Disclaimer Dialog */}
      <LiabilityDisclaimer 
        open={showDisclaimer} 
        onAccept={onDisclaimerAccept}
        onDecline={onDisclaimerDecline}
      />
    </div>
  );

  // NEW PROJECT FLOW
  const renderNewProject = () => {
    // Conflict mode steps
    const conflictSteps = [
      { id: 'setup', title: 'The Situation', subtitle: 'Name the situation and who is involved', color: 'from-slate-900 to-black', borderColor: 'border-white/10' },
      { id: 'blue', title: 'Blue · Observe', subtitle: 'Each person shares what they observed—just the facts', color: 'from-blue-950 to-black', borderColor: 'border-blue-500/30', icon: ObserveIcon, hexColor: '#3B82F6', isBespoke: true },
      { id: 'red', title: 'Red · Feel', subtitle: 'Feelings are messengers — what emotions arose? They point to your needs.', color: 'from-red-950 to-black', borderColor: 'border-red-500/30', icon: FeelIcon, hexColor: '#EF4444', isBespoke: true },
      { id: 'green', title: 'Green · Need', subtitle: 'What universal needs are beneath those feelings? Unmet needs create painful feelings.', color: 'from-emerald-950 to-black', borderColor: 'border-emerald-500/30', icon: NeedIcon, hexColor: '#10B981', isBespoke: true },
      { id: 'understand', title: 'The Bridge', subtitle: 'Genuine understanding before solutions', color: 'from-purple-950 to-black', borderColor: 'border-purple-500/30', icon: BridgeIcon, hexColor: '#8B5CF6', isBespoke: true },
      { id: 'gold', title: 'Gold · Resolution', subtitle: 'Together, find the golden bow', color: 'from-amber-950 to-black', borderColor: 'border-amber-500/40', icon: GoldIcon, hexColor: '#F59E0B', isBespoke: true },
    ];
    
    // Appreciation mode steps - celebrating and receiving gratitude
    const appreciationSteps = [
      { id: 'setup', title: 'The Moment', subtitle: 'Name this beautiful moment and who contributed', color: 'from-emerald-950 to-black', borderColor: 'border-emerald-500/20' },
      { id: 'blue', title: 'Blue · Witness', subtitle: 'Like a film camera, write down objectively what happened? What did you observe without judgment?', color: 'from-blue-950 to-black', borderColor: 'border-blue-500/30', icon: ObserveIcon, hexColor: '#3B82F6', isBespoke: true },
      { id: 'red', title: 'Red · Feel', subtitle: 'What feelings of joy, gratitude, or connection arose?', color: 'from-rose-950 to-black', borderColor: 'border-rose-400/30', icon: FeelIcon, hexColor: '#FB7185', isBespoke: true },
      { id: 'green', title: 'Green · Nourish', subtitle: 'What needs were beautifully met? What flourished?', color: 'from-emerald-950 to-black', borderColor: 'border-emerald-500/30', icon: NeedIcon, hexColor: '#10B981', isBespoke: true },
      { id: 'understand', title: 'The Receiving', subtitle: 'Receive this gift without deflecting or minimizing', color: 'from-purple-950 to-black', borderColor: 'border-purple-500/30', icon: BridgeIcon, hexColor: '#8B5CF6', isBespoke: true },
      { id: 'gold', title: 'Gold · Celebration', subtitle: 'Honor this moment of human connection', color: 'from-amber-950 to-black', borderColor: 'border-amber-500/40', icon: GoldIcon, hexColor: '#F59E0B', isBespoke: true },
    ];
    
    const steps = experienceMode === 'appreciation' ? appreciationSteps : conflictSteps;

    const step = steps[currentStep];
    const Icon = step.icon || RefreshIcon;
    const hasParticipants = formState.participants.length > 0;

    // Helper to add chip to participant or self
    // FIXED: Use ref to get latest activeParticipantId (fixes stale closure bug)
    const addChipToInput = (chip, field) => {
      setFormState(prev => {
        // Get the latest activeParticipantId from the ref (not the stale closure)
        const currentActiveId = activeParticipantIdRef.current;
        
        // Determine target ID inside the functional update to get latest state
        let targetId = null;
        let targetName = null;
        
        if (prev.participants.length > 0) {
          if (currentActiveId && prev.participantInputs[currentActiveId]) {
            // Use the currently active participant
            targetId = currentActiveId;
            const participant = prev.participants.find(p => p.id === targetId);
            targetName = participant?.name || 'Participant';
          } else {
            // If no one is selected, select the first one automatically
            targetId = prev.participants[0].id;
            targetName = prev.participants[0].name;
            // Schedule activeParticipantId update after this state update
            setTimeout(() => {
              setActiveParticipantId(targetId);
            }, 0);
          }
        } else {
          targetId = 'self';
          targetName = 'You';
        }

        // Make sure targetId is valid
        if (!targetId) {
          console.warn('No target ID for chip selection');
          return prev;
        }

        const newInputs = { ...prev.participantInputs };
        const current = newInputs[targetId]?.[field] || '';
        
        // Only add if not already present
        if (!current.toLowerCase().includes(chip.toLowerCase())) {
          newInputs[targetId] = {
            ...(newInputs[targetId] || { blue: '', red: '', green: '' }),
            [field]: current ? `${current}, ${chip}` : chip
          };
          // Removed toast - cleaner UX without popup spam
        }
        
        return { ...prev, participantInputs: newInputs };
      });
    };

    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
        {/* Gold step spiral orb background */}
        {currentStep === 5 && formState.gold && (
          <GoldOrbBackground intensity={Math.min(formState.gold.length / 50, 1)} />
        )}
        
        <div className="w-full max-w-lg relative z-10">
          {/* Top toolbar - minimal and clean */}
          <div className="flex items-center justify-between mb-3 px-1">
            {/* Left - Dashboard */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (formState.title || Object.keys(formState.participantInputs).length > 0) {
                  toast.info('Your work is auto-saved to your dashboard', { duration: 2000 });
                }
                setView('board');
              }}
              className="text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 flex items-center gap-1 px-2 py-1"
              title="Return to Dashboard - Your work is saved"
              data-testid="dashboard-btn"
            >
              <HomeIcon size={14} color="#FBBF24" />
              <span className="text-xs">Dashboard</span>
            </Button>
            
            {/* Center - Step indicator */}
            <div className="flex items-center gap-1 text-xs text-white/50">
              <span className="hidden sm:inline">{step.title}</span>
            </div>
            
            {/* Right - M.ai.ry, SOS, Process Helper, and Wisdom */}
            <div className="flex items-center gap-1">
              <a
                href="https://mairy.io"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-purple-500/10 transition-colors group"
                title="M.ai.ry - Wisdom Bot"
                data-testid="mairy-btn"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" fill="none" className="opacity-50 group-hover:opacity-100 transition-opacity">
                  <circle cx="24" cy="24" r="16" stroke="#A855F7" strokeWidth="1.5" strokeOpacity="0.6" fill="#A855F7" fillOpacity="0.1" />
                  <path d="M14 32V18L20 26L24 20L28 26L34 18V32" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </a>
              <SOSTriggerButton 
                onClick={() => setShowTimeoutSOS(true)}
              />
              <button
                onClick={() => setShowOFNRHelper(true)}
                className="p-2 rounded-full hover:bg-amber-500/10 transition-colors"
                title="How ElchmE Works"
                data-testid="ofnr-helper-btn"
              >
                <InsightIcon size={18} color="#FBBF24" />
              </button>
              <WisdomGuide 
                currentStep={currentStep}
                currentInput={
                  currentStep === 1 ? formState.participantInputs[activeParticipantId || 'self']?.blue :
                  currentStep === 2 ? formState.participantInputs[activeParticipantId || 'self']?.red :
                  currentStep === 3 ? formState.participantInputs[activeParticipantId || 'self']?.green :
                  formState.gold
                }
                participants={formState.participants}
                onSuggestionApply={(suggestion) => {
                  const field = currentStep === 1 ? 'blue' : currentStep === 2 ? 'red' : currentStep === 3 ? 'green' : null;
                  if (field && (activeParticipantId || formState.participants.length === 0)) {
                    updateParticipantInput(activeParticipantId || 'self', field, suggestion);
                  }
                }}
              />
            </div>
          </div>

          <div className={`bg-gradient-to-b ${step.color} border ${step.borderColor} rounded-2xl shadow-2xl p-5`}>
            
            {/* Progress - now as secondary indicator */}
            <div className="flex justify-center gap-1.5 mb-5">
              {steps.map((_, idx) => (
                <div key={idx} className={`h-1 rounded-full transition-all duration-500 ${
                  idx < currentStep ? 'w-6 bg-amber-500' : idx === currentStep ? 'w-10 bg-white' : 'w-6 bg-white/20'
                }`} />
              ))}
            </div>

            {/* Header */}
            <div className="text-center mb-5">
              {step.isBespoke ? (
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <Icon size={40} color={step.hexColor} />
                </div>
              ) : (
                <Icon className={`w-8 h-8 mx-auto mb-3 ${
                  currentStep === 1 ? 'text-blue-400' : currentStep === 2 ? 'text-red-400' :
                  currentStep === 3 ? 'text-emerald-400' : currentStep === 4 ? 'text-purple-400' : 
                  currentStep === 5 ? 'text-amber-400' : 'text-white/60'
                }`} />
              )}
              <h2 className="text-xl font-light text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{step.title}</h2>
              <p className="text-xs text-white/50">{step.subtitle}</p>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {currentStep === 0 && (
                <>
                  <div>
                    <label className="text-xs text-white/40 mb-1 block">What's the friction, conflict, or moment of appreciation?</label>
                    <Input
                      value={formState.title}
                      onChange={(e) => setFormState({ ...formState, title: e.target.value })}
                      placeholder="What's the friction, conflict, appreciation or connection?"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 mb-2 block">Who is involved?</label>
                    <UserSelector
                      selectedUsers={formState.participants}
                      onSelect={addParticipant}
                      onRemove={removeParticipant}
                    />
                  </div>
                </>
              )}

              {currentStep === 1 && (
                <div className="space-y-3">
                  {hasParticipants ? (
                    formState.participants.map(person => (
                      <PersonInput
                        key={person.id}
                        person={person}
                        color="blue"
                        value={formState.participantInputs[person.id]?.blue || ''}
                        onChange={(val) => updateParticipantInput(person.id, 'blue', val)}
                        onFocus={() => setActiveParticipantId(person.id)}
                        isActive={activeParticipantId === person.id}
                        placeholder="What did you observe? Just the facts..."
                        label="observes"
                      />
                    ))
                  ) : (
                    <>
                      {/* Show context: what situation this is about */}
                      {formState.title && (
                        <div className="mb-3 p-3 bg-black/20 rounded-lg border border-blue-500/20">
                          <p className="text-blue-300/60 text-xs mb-1">Related to:</p>
                          <p className="text-white/80 text-sm font-medium">{formState.title}</p>
                        </div>
                      )}
                      
                      <Textarea
                        value={formState.participantInputs['self']?.blue || ''}
                        onChange={(e) => updateParticipantInput('self', 'blue', e.target.value)}
                        placeholder={experienceMode === 'appreciation' 
                          ? "What specific actions did you witness that touched you?" 
                          : "What would a camera record?"}
                        className="bg-blue-900/20 border-blue-500/30 text-white placeholder:text-blue-300/30 min-h-[120px]"
                      />
                      <GrayWordsInlineAnalyzer 
                        text={formState.participantInputs['self']?.blue || ''} 
                        showDetails={true}
                      />
                      {/* I Choose To Exercise - Power of Agency */}
                      <button
                        onClick={() => setShowIChooseTo(true)}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm transition-all"
                      >
                        <SparkleIcon size={16} color="#60A5FA" />
                        <span>Explore the Power of Choice</span>
                      </button>
                    </>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-3">
                  {/* Empathy Mode Toggle - only show if there are multiple participants */}
                  {hasParticipants && formState.participants.length > 0 && (
                    <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-black/20 rounded-lg">
                      <button
                        onClick={() => { setEmpathyMode(false); setEmpathyTargetId(null); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                          !empathyMode 
                            ? 'bg-red-500/30 text-red-200 border border-red-400/50' 
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <FeelIcon size={12} color={!empathyMode ? "#F87171" : "#9CA3AF"} />
                        My Feelings
                      </button>
                      <button
                        onClick={() => setEmpathyMode(true)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                          empathyMode 
                            ? 'bg-purple-500/30 text-purple-200 border border-purple-400/50' 
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <HeartIcon size={12} color={empathyMode ? "#A78BFA" : "#9CA3AF"} />
                        Empathy Inquiry
                      </button>
                    </div>
                  )}
                  
                  {!empathyMode ? (
                    <>
                      {/* Original feelings input */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(experienceMode === 'romance' ? ROMANCE_FEELING_CHIPS : experienceMode === 'appreciation' ? APPRECIATION_FEELING_CHIPS : FEELING_CHIPS).map(f => (
                          <button 
                            key={f} 
                            onClick={() => addChipToInput(f, 'red')}
                            className={`px-2 py-1 rounded-full border text-[10px] transition-all ${
                              experienceMode === 'appreciation'
                                ? 'border-rose-400/30 text-rose-200/70 hover:bg-rose-400/20 hover:border-rose-300/50'
                                : 'border-red-500/30 text-red-200/70 hover:bg-red-500/20 hover:border-red-400/50'
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                      {hasParticipants ? (
                        <>
                          <p className="text-[10px] text-white/30 text-center mb-2">Tap a box below to select who is speaking</p>
                          {formState.participants.map(person => (
                            <PersonInput
                              key={person.id}
                              person={person}
                              color="red"
                              value={formState.participantInputs[person.id]?.red || ''}
                              onChange={(val) => updateParticipantInput(person.id, 'red', val)}
                              onFocus={() => setActiveParticipantId(person.id)}
                              isActive={activeParticipantId === person.id}
                              placeholder="I feel..."
                              label="feels"
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          <Textarea
                            value={formState.participantInputs['self']?.red || ''}
                            onChange={(e) => updateParticipantInput('self', 'red', e.target.value)}
                            placeholder={experienceMode === 'romance'
                              ? "Explore this feeling deeply... What do you feel in your heart? Your body? Let the words flow without holding back."
                              : experienceMode === 'appreciation' 
                              ? "I feel grateful, touched, moved, joyful... Tell the story of these feelings." 
                              : "I feel... (Explore what's beneath the surface. What's this emotion trying to tell you?)"}
                            className={`${
                              experienceMode === 'romance' ? 'bg-rose-900/30 border-rose-400/40' :
                              experienceMode === 'appreciation' ? 'bg-rose-900/20 border-rose-400/30' : 'bg-red-900/20 border-red-500/30'
                            } text-white min-h-[180px] leading-relaxed`}
                            data-testid="feelings-textarea"
                          />
                          <GrayWordsInlineAnalyzer 
                            text={formState.participantInputs['self']?.red || ''} 
                            showDetails={true}
                          />
                          
                          {/* Observation Reference - What you said in Blue step */}
                          {formState.participantInputs['self']?.blue && (
                            <div className="mt-3 p-3 bg-blue-900/10 rounded-lg border border-blue-500/20 relative overflow-hidden">
                              {/* Fun glowing icon indicator */}
                              <div className="absolute -top-2 -left-2 w-12 h-12 flex items-center justify-center">
                                <div className="absolute inset-0 bg-blue-500/30 rounded-full animate-ping" />
                                <div className="absolute inset-0 bg-blue-500/40 rounded-full" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                                <ObserveIcon size={20} color="#60A5FA" className="relative z-10" style={{ filter: 'drop-shadow(0 0 8px #60A5FA)' }} />
                              </div>
                              
                              <div className="flex items-center gap-2 mb-2 ml-8">
                                <ObserveIcon size={14} color="#60A5FA" />
                                <span className="text-blue-300/80 text-xs font-medium">What You Observed (from Blue step)</span>
                              </div>
                              <p className="text-white/70 text-sm leading-relaxed italic ml-8">
                                "{formState.participantInputs['self'].blue.substring(0, 150)}{formState.participantInputs['self'].blue.length > 150 ? '...' : ''}"
                              </p>
                              <p className="text-red-200/50 text-[10px] mt-2 text-center">
                                ↑ These observations sparked the feelings below ↓
                              </p>
                            </div>
                          )}
                          
                          {/* Friendly objectivity reminder - auto-dismisses after 7 seconds */}
                          {showObjectivityReminder && (
                            <div className="mt-3 p-3 bg-blue-900/10 rounded-lg border border-blue-500/20 transition-opacity duration-500" style={{ opacity: showObjectivityReminder ? 1 : 0 }}>
                              <p className="text-blue-200/80 text-xs leading-relaxed text-center">
                                💙 <em>Remember: Name your feelings without blame or judgment.</em> Check the body, not the courtroom. Feelings are data, not weapons. 💙
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    /* Empathy Inquiry Mode - guess what others might be feeling */
                    <div className="space-y-4">
                      <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                        <p className="text-purple-200/90 text-sm mb-1">
                          <HeartIcon size={14} color="#A78BFA" className="inline mr-1" />
                          Empathy Inquiry
                        </p>
                        <p className="text-purple-200/60 text-xs">
                          With genuine curiosity, wonder what the other might be feeling...
                        </p>
                      </div>
                      
                      {/* Select who to empathize with */}
                      <div className="flex flex-wrap gap-2 justify-center">
                        {formState.participants.map(person => (
                          <button
                            key={person.id}
                            onClick={() => setEmpathyTargetId(person.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              empathyTargetId === person.id
                                ? 'bg-purple-500/30 text-purple-200 border border-purple-400/50'
                                : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                            }`}
                          >
                            {person.name}
                          </button>
                        ))}
                      </div>
                      
                      {empathyTargetId && (
                        <div className="space-y-2">
                          <p className="text-purple-200/70 text-xs text-center">
                            "I wonder if {formState.participants.find(p => p.id === empathyTargetId)?.name} might be feeling..."
                          </p>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {FEELING_CHIPS.map(f => (
                              <button 
                                key={f} 
                                onClick={() => {
                                  const current = formState.participantInputs[activeParticipantId || 'self']?.empathyGuesses?.[empathyTargetId]?.red || '';
                                  const newValue = current ? `${current}, ${f.toLowerCase()}` : f.toLowerCase();
                                  updateEmpathyGuess(activeParticipantId || 'self', empathyTargetId, 'red', newValue);
                                }}
                                className="px-2 py-1 rounded-full border border-purple-500/30 text-[10px] text-purple-200/70 hover:bg-purple-500/20 hover:border-purple-400/50 transition-all"
                              >
                                {f}
                              </button>
                            ))}
                          </div>
                          <Textarea
                            value={formState.participantInputs[activeParticipantId || 'self']?.empathyGuesses?.[empathyTargetId]?.red || ''}
                            onChange={(e) => updateEmpathyGuess(activeParticipantId || 'self', empathyTargetId, 'red', e.target.value)}
                            placeholder={`Are you feeling... ? (curious, not certain)`}
                            className="bg-purple-900/20 border-purple-500/30 text-white min-h-[80px]"
                          />
                          <p className="text-purple-300/50 text-[10px] italic text-center">
                            Remember: This is a guess, not a statement. We check with them, not tell them.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-3">
                  {/* Empathy Mode Toggle - only show if there are multiple participants */}
                  {hasParticipants && formState.participants.length > 0 && (
                    <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-black/20 rounded-lg">
                      <button
                        onClick={() => { setEmpathyMode(false); setEmpathyTargetId(null); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                          !empathyMode 
                            ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/50' 
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <NeedIcon size={12} color={!empathyMode ? "#34D399" : "#9CA3AF"} />
                        My Needs
                      </button>
                      <button
                        onClick={() => setEmpathyMode(true)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                          empathyMode 
                            ? 'bg-purple-500/30 text-purple-200 border border-purple-400/50' 
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <HeartIcon size={12} color={empathyMode ? "#A78BFA" : "#9CA3AF"} />
                        Empathy Inquiry
                      </button>
                    </div>
                  )}
                  
                  {!empathyMode ? (
                    <>
                      {/* Original needs input */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(experienceMode === 'romance' ? ROMANCE_NEED_CHIPS : experienceMode === 'appreciation' ? APPRECIATION_NEED_CHIPS : NEED_CHIPS).map(n => (
                          <button 
                            key={n} 
                            onClick={() => addChipToInput(n, 'green')}
                            className="px-2 py-1 rounded-full border border-emerald-500/30 text-[10px] text-emerald-200/70 hover:bg-emerald-500/20 hover:border-emerald-400/50 transition-all"
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      {hasParticipants ? (
                        <>
                          <p className="text-[10px] text-white/30 text-center mb-2">Tap a box below to select who is speaking</p>
                          {formState.participants.map(person => (
                            <PersonInput
                              key={person.id}
                              person={person}
                              color="green"
                              value={formState.participantInputs[person.id]?.green || ''}
                              onChange={(val) => updateParticipantInput(person.id, 'green', val)}
                              onFocus={() => setActiveParticipantId(person.id)}
                              isActive={activeParticipantId === person.id}
                              placeholder={experienceMode === 'appreciation' ? "This nourished my need for..." : "I need..."}
                              label={experienceMode === 'appreciation' ? "was nourished" : "needs"}
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          <Textarea
                            value={formState.participantInputs['self']?.green || ''}
                            onChange={(e) => updateParticipantInput('self', 'green', e.target.value)}
                            placeholder={experienceMode === 'romance'
                              ? "What do you need from this connection? Tell the story of this need. Why does it matter so deeply to you?"
                              : experienceMode === 'appreciation'
                              ? "This nourished my need for... (What need was beautifully met? Explore the fullness of it.)"
                              : "I need / I value... (Don't hold back. Let this need speak its truth.)"}
                            className={`${
                              experienceMode === 'romance' ? 'bg-pink-900/30 border-pink-400/40' :
                              experienceMode === 'appreciation' ? 'bg-emerald-900/25 border-emerald-400/35' :
                              'bg-emerald-900/20 border-emerald-500/30'
                            } text-white min-h-[180px] leading-relaxed`}
                            data-testid="needs-textarea"
                          />
                          
                          {/* Feelings Reference - What you said in Red step */}
                          {formState.participantInputs['self']?.red && (
                            <div className="mt-3 p-3 bg-red-900/10 rounded-lg border border-red-500/20 relative overflow-hidden">
                              {/* Fun glowing icon indicator */}
                              <div className="absolute -top-2 -left-2 w-12 h-12 flex items-center justify-center">
                                <div className="absolute inset-0 bg-red-500/30 rounded-full animate-ping" />
                                <div className="absolute inset-0 bg-red-500/40 rounded-full" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                                <HeartIcon size={20} color="#EF4444" className="relative z-10" style={{ filter: 'drop-shadow(0 0 8px #EF4444)' }} />
                              </div>
                              
                              <div className="flex items-center gap-2 mb-2 ml-8">
                                <FeelIcon size={14} color="#F87171" />
                                <span className="text-red-300/80 text-xs font-medium">Your Feelings (from Red step)</span>
                              </div>
                              <p className="text-white/70 text-sm leading-relaxed italic ml-8">
                                "{formState.participantInputs['self'].red.substring(0, 150)}{formState.participantInputs['self'].red.length > 150 ? '...' : ''}"
                              </p>
                              <p className="text-emerald-200/50 text-[10px] mt-2 text-center">
                                ↑ These feelings point to the needs below ↓
                              </p>
                            </div>
                          )}
                          
                          <GrayWordsInlineAnalyzer 
                            text={formState.participantInputs['self']?.green || ''} 
                            showDetails={true}
                          />
                        </>
                      )}
                      
                      {/* Belonging Song Generator Button */}
                      <div className="pt-3 border-t border-emerald-500/20 mt-3">
                        <p className="text-xs text-emerald-400/70 mb-2 flex items-center gap-2">
                          <NeedIcon size={12} color="#10B981" />
                          What do you need most to grow in belonging?
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowNeedsSong(true)}
                          className="w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          data-testid="belonging-song-btn"
                        >
                          <MusicIcon size={16} color="#EC4899" className="mr-2" />
                          Create Your Belonging Song
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Empathy Inquiry Mode - guess what others might need */
                    <div className="space-y-4">
                      <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                        <p className="text-purple-200/90 text-sm mb-1">
                          <HeartIcon size={14} color="#A78BFA" className="inline mr-1" />
                          Empathy Inquiry
                        </p>
                        <p className="text-purple-200/60 text-xs">
                          With genuine curiosity, wonder what the other might be needing...
                        </p>
                      </div>
                      
                      {/* Select who to empathize with */}
                      <div className="flex flex-wrap gap-2 justify-center">
                        {formState.participants.map(person => (
                          <button
                            key={person.id}
                            onClick={() => setEmpathyTargetId(person.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              empathyTargetId === person.id
                                ? 'bg-purple-500/30 text-purple-200 border border-purple-400/50'
                                : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                            }`}
                          >
                            {person.name}
                          </button>
                        ))}
                      </div>
                      
                      {empathyTargetId && (
                        <div className="space-y-2">
                          <p className="text-purple-200/70 text-xs text-center">
                            "I wonder if {formState.participants.find(p => p.id === empathyTargetId)?.name} might be needing..."
                          </p>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {NEED_CHIPS.map(n => (
                              <button 
                                key={n} 
                                onClick={() => {
                                  const current = formState.participantInputs[activeParticipantId || 'self']?.empathyGuesses?.[empathyTargetId]?.green || '';
                                  const newValue = current ? `${current}, ${n.toLowerCase()}` : n.toLowerCase();
                                  updateEmpathyGuess(activeParticipantId || 'self', empathyTargetId, 'green', newValue);
                                }}
                                className="px-2 py-1 rounded-full border border-purple-500/30 text-[10px] text-purple-200/70 hover:bg-purple-500/20 hover:border-purple-400/50 transition-all"
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                          <Textarea
                            value={formState.participantInputs[activeParticipantId || 'self']?.empathyGuesses?.[empathyTargetId]?.green || ''}
                            onChange={(e) => updateEmpathyGuess(activeParticipantId || 'self', empathyTargetId, 'green', e.target.value)}
                            placeholder={`Do you need... ? (curious, not certain)`}
                            className="bg-purple-900/20 border-purple-500/30 text-white min-h-[80px]"
                          />
                          <p className="text-purple-300/50 text-[10px] italic text-center">
                            Remember: This is a guess, not a statement. We check with them, not tell them.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* NEW: Understanding/Bridge Step */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-4 mb-4">
                    <p className="text-purple-200/90 text-sm leading-relaxed mb-3">
                      <strong>Pause.</strong> Before finding solutions, we choose to truly understand each other first.
                    </p>
                    <p className="text-purple-200/60 text-xs leading-relaxed">
                      This invites <strong>authentic, genuine concern</strong> for the other—not analyzing, moralizing, 
                      criticizing, advising, suggesting, or telling them what to feel or need.
                    </p>
                  </div>

                  {/* Summary of what was shared */}
                  <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                    <h4 className="text-white/60 text-xs uppercase tracking-wide mb-3">What We've Shared</h4>
                    {hasParticipants ? (
                      formState.participants.map(person => (
                        <div key={person.id} className="mb-3 pb-3 border-b border-white/5 last:border-0 last:mb-0 last:pb-0">
                          <p className="text-amber-400 font-medium text-xs mb-2">{person.name}</p>
                          <div className="grid gap-1 text-[11px]">
                            <p className="text-blue-300/70">
                              <ObserveIcon size={12} color="#60A5FA" className="inline mr-1" />
                              {formState.participantInputs[person.id]?.blue || '...'}
                            </p>
                            <p className="text-red-300/70">
                              <FeelIcon size={12} color="#F87171" className="inline mr-1" />
                              {formState.participantInputs[person.id]?.red || '...'}
                            </p>
                            <p className="text-emerald-300/70">
                              <NeedIcon size={12} color="#34D399" className="inline mr-1" />
                              {formState.participantInputs[person.id]?.green || '...'}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-[11px] space-y-1">
                        <p className="text-blue-300/70">Observe: {formState.participantInputs['self']?.blue || '...'}</p>
                        <p className="text-red-300/70">Feel: {formState.participantInputs['self']?.red || '...'}</p>
                        <p className="text-emerald-300/70">Need: {formState.participantInputs['self']?.green || '...'}</p>
                      </div>
                    )}
                  </div>

                  {/* Spiral Interchange Visualization */}
                  <SpiralInterchange
                    yourFeelings={formState.participantInputs['self']?.red || ''}
                    yourNeeds={formState.participantInputs['self']?.green || ''}
                    theirFeelings={activeParticipantId && activeParticipantId !== 'self' 
                      ? formState.participantInputs[activeParticipantId]?.empathyGuesses?.self?.red || ''
                      : formState.participantInputs['self']?.empathyGuesses?.[activeParticipantId]?.red || ''}
                    theirNeeds={activeParticipantId && activeParticipantId !== 'self'
                      ? formState.participantInputs[activeParticipantId]?.empathyGuesses?.self?.green || ''
                      : formState.participantInputs['self']?.empathyGuesses?.[activeParticipantId]?.green || ''}
                    intensity="medium"
                    mode={experienceMode}
                  />

                  {/* Checkboxes for understanding */}
                  <div className="space-y-3 pt-2">
                    <p className="text-white/40 text-xs">Before proceeding, confirm:</p>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" className="mt-0.5 accent-purple-500 w-4 h-4" />
                      <span className="text-white/70 text-sm group-hover:text-white/90">
                        I am genuinely curious about understanding the other's true feelings and needs
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" className="mt-0.5 accent-purple-500 w-4 h-4" />
                      <span className="text-white/70 text-sm group-hover:text-white/90">
                        I will not analyze, moralize, criticize, or advise—only understand
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" className="mt-0.5 accent-purple-500 w-4 h-4" />
                      <span className="text-white/70 text-sm group-hover:text-white/90">
                        I approach this from a place of authentic compassion, even if I disagree
                      </span>
                    </label>
                  </div>

                  <div className="bg-purple-900/10 border border-purple-500/10 rounded-lg p-3 mt-4">
                    <p className="text-purple-200/50 text-[10px] text-center italic">
                      "Be genuinely, persistently, relentlessly curious about their experience. Not to agree—to understand."
                    </p>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <>
                  {/* Complete Journey Summary - All Participant Inputs */}
                  <ParticipantJourneySummary 
                    formState={formState} 
                    hasParticipants={formState.participants.length > 0} 
                  />

                  {/* Thank You Exercise for Appreciation Mode */}
                  {experienceMode === 'appreciation' && (
                    <div className="mt-4 p-4 bg-gradient-to-br from-rose-900/20 to-amber-900/20 rounded-xl border border-rose-500/30">
                      <div className="flex items-center gap-2 mb-3">
                        <HeartIcon size={16} color="#FB7185" />
                        <h3 className="text-rose-300 font-medium text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                          Thank You: Celebrating What Was Given
                        </h3>
                      </div>
                      <p className="text-white/60 text-xs mb-4 leading-relaxed">
                        Express appreciation using the OFNR format to celebrate rather than judge. 
                        This is not a compliment—it's acknowledgment of how someone's actions met your needs.
                      </p>
                      
                      <div className="space-y-3">
                        {/* Observation - What they did */}
                        <div>
                          <label className="text-xs text-blue-300/80 mb-1 block flex items-center gap-1">
                            <ObserveIcon size={10} color="#60A5FA" />
                            This is what you did...
                          </label>
                          <Textarea
                            value={formState.participantInputs[activeParticipantId || 'self']?.appreciationObservation || ''}
                            onChange={(e) => updateParticipantInput(
                              activeParticipantId || 'self',
                              'appreciationObservation',
                              e.target.value
                            )}
                            placeholder="Describe the specific action without evaluation (e.g., 'You stayed with me during that difficult conversation')"
                            className="bg-blue-900/10 border-blue-500/20 text-white placeholder:text-white/30 min-h-[60px] text-sm"
                            data-testid="thank-you-observation"
                          />
                        </div>
                        
                        {/* Feeling - What I feel */}
                        <div>
                          <label className="text-xs text-rose-300/80 mb-1 block flex items-center gap-1">
                            <FeelIcon size={10} color="#FB7185" />
                            This is what I feel...
                          </label>
                          <Textarea
                            value={formState.participantInputs[activeParticipantId || 'self']?.appreciationFeeling || ''}
                            onChange={(e) => updateParticipantInput(
                              activeParticipantId || 'self',
                              'appreciationFeeling',
                              e.target.value
                            )}
                            placeholder="Name the feelings that arise (e.g., 'I feel grateful, touched, and at peace')"
                            className="bg-rose-900/10 border-rose-500/20 text-white placeholder:text-white/30 min-h-[60px] text-sm"
                            data-testid="thank-you-feeling"
                          />
                        </div>
                        
                        {/* Need Met - The need that WAS met */}
                        <div>
                          <label className="text-xs text-emerald-300/80 mb-1 block flex items-center gap-1">
                            <NeedIcon size={10} color="#10B981" />
                            This is the need of mine that WAS met...
                          </label>
                          <Textarea
                            value={formState.participantInputs[activeParticipantId || 'self']?.appreciationNeedMet || ''}
                            onChange={(e) => updateParticipantInput(
                              activeParticipantId || 'self',
                              'appreciationNeedMet',
                              e.target.value
                            )}
                            placeholder="Which of your needs were fulfilled? (e.g., 'My need for connection and trust was beautifully met')"
                            className="bg-emerald-900/10 border-emerald-500/20 text-white placeholder:text-white/30 min-h-[60px] text-sm"
                            data-testid="thank-you-need-met"
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-white/50 text-xs italic">
                          "Expression of appreciation solely for celebration is the goal. 
                          We can celebrate together without superiority or false humility."
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="text-xs text-amber-400/80 mb-2 block flex items-center gap-2">
                      <StarIcon size={12} color="#FBBF24" />
                      {experienceMode === 'appreciation' ? 'Celebration & Next Steps' : 'The Golden Resolution'}
                    </label>
                    <Textarea
                      value={formState.gold}
                      onChange={(e) => setFormState({ ...formState, gold: e.target.value })}
                      placeholder={
                        experienceMode === 'appreciation'
                          ? "How would you like to celebrate this? What comes next?"
                          : "What little ask can you offer? Add as many ideas as you want—free flow, focus on solutions that serve everyone..."
                      }
                      className="bg-amber-900/20 border-amber-500/30 text-white placeholder:text-amber-300/40 min-h-[100px]"
                      data-testid="gold-resolution-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Owner</label>
                      <Input
                        value={formState.owner}
                        onChange={(e) => setFormState({ ...formState, owner: e.target.value })}
                        placeholder="Who holds this?"
                        className="bg-white/5 border-white/20 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Check-in</label>
                      <Input
                        type="date"
                        value={formState.checkInDate}
                        onChange={(e) => setFormState({ ...formState, checkInDate: e.target.value })}
                        className="bg-white/5 border-white/20 text-white text-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Preview Journey Summary before forging */}
                  <div className="mt-4">
                    <GoldenSummaryButton
                      onClick={() => setShowGoldenSummary(true)}
                      label="Preview Your Journey"
                    />
                    <p className="text-center text-white/40 text-xs mt-2">
                      Review your complete OFNR journey before saving
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Navigation */}
            <div className="mt-6 flex gap-3">
              <Button 
                onClick={() => currentStep === 0 ? setView('landing') : setCurrentStep(currentStep - 1)} 
                variant="outline" 
                className="w-1/3 border-white/20 text-white/60 hover:bg-white/10"
              >
                <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-1" /> Back
              </Button>

              {currentStep < 5 ? (
                <Button 
                  onClick={handleNextStep}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white"
                  disabled={currentStep === 0 && !formState.title}
                >
                  Continue <ArrowIcon size={16} color="#9CA3AF" direction="right" className="ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={createProject}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold"
                  disabled={!formState.gold}
                >
                  <StarIcon size={16} color="#FBBF24" className="mr-2" /> Forge Gold
                </Button>
              )}
            </div>
            
            {/* Helper tools row - inside the card */}
            <div className="mt-4 flex items-center justify-center gap-2 border-t border-white/10 pt-4">
              {/* Observations Practice - shown on Observe step */}
              {currentStep === 1 && (
                <ObservationsExercise 
                  triggerClassName="text-blue-400/70 hover:text-blue-400"
                />
              )}
              
              {/* Feelings/Needs Helper Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFeelingsHelper(true)}
                className="text-zinc-400 hover:text-emerald-400 text-xs flex items-center gap-1"
                title="Feelings & Needs Reference"
                data-testid="feelings-helper-btn"
              >
                <FeelIcon size={14} color="#EF4444" />
                <span className="hidden sm:inline">Feelings</span>
              </Button>
              
              {/* Instant Summary Button */}
              <SummaryTriggerButton 
                onClick={() => setShowInstantSummary(true)}
              />
              
              {formState.participants.length > 0 && (
                <SpiralCouncilSOS 
                  sessionId={currentProject?.id || 'new'}
                  participants={formState.participants}
                  currentUserId={activeParticipantId}
                />
              )}
            </div>

            {/* OFNR Step Indicators - Below Experience */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {steps.slice(1).map((step, idx) => {
                const stepIndex = idx + 1;
                const isActive = currentStep === stepIndex;
                const isCompleted = currentStep > stepIndex;
                const StepIcon = step.icon || SparkleIcon;
                
                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => stepIndex <= currentStep && setCurrentStep(stepIndex)}
                      disabled={stepIndex > currentStep}
                      className={`
                        w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300
                        ${isActive 
                          ? 'bg-white/15 border-2 scale-110 shadow-lg' 
                          : isCompleted 
                            ? 'bg-white/10 border border-white/30 hover:scale-105 cursor-pointer' 
                            : 'bg-white/5 border border-white/10 opacity-40 cursor-not-allowed'
                        }
                      `}
                      style={{ 
                        borderColor: isActive ? step.hexColor : undefined,
                        boxShadow: isActive ? `0 0 15px ${step.hexColor}30` : undefined
                      }}
                      title={step.title}
                      data-testid={`ofnr-step-${step.id}`}
                    >
                      <StepIcon 
                        size={20} 
                        color={isActive || isCompleted ? step.hexColor : '#ffffff30'} 
                      />
                    </button>
                    {idx < steps.length - 2 && (
                      <div className={`w-4 h-px ${isCompleted ? 'bg-white/30' : 'bg-white/10'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Gray Words Preflection Dialog */}
        <GrayWordsPreflection
          isOpen={showGrayWordsPreflection}
          onClose={() => setShowGrayWordsPreflection(false)}
          onProceed={handleGrayWordsComplete}
        />

        {/* NVC Wisdom Popup - periodic reminders */}
        {currentWisdom && (
          <WisdomPopup 
            wisdom={currentWisdom} 
            onDismiss={dismissWisdom} 
          />
        )}

        {/* Soul Reminder Banner - prominent recurring message */}
        <SoulReminderBanner 
          show={showSoulReminder} 
          onDismiss={dismissSoulReminder}
          variant="default"
        />

        {/* Needs Song Generator Dialog */}
        <NeedsSongDialog
          open={showNeedsSong}
          onOpenChange={setShowNeedsSong}
          onComplete={(needs) => {
            setUserBelongingNeeds(needs);
            toast.success('Your Belonging Song has been created');
          }}
        />

        {/* OFNR Process Helper */}
        <OFNRProcessHelper
          isOpen={showOFNRHelper}
          onClose={() => setShowOFNRHelper(false)}
        />

        {/* Feelings & Needs Helper Popup */}
        <FeelingsNeedsHelper
          isOpen={showFeelingsHelper}
          onClose={() => setShowFeelingsHelper(false)}
          mode="modal"
        />

        {/* I Choose To Exercise - Power of Agency */}
        <IChooseToExercise
          isOpen={showIChooseTo}
          onClose={() => setShowIChooseTo(false)}
        />
        
        {/* Mindful SOS - Peaceful Pause */}
        <TimeoutSOS
          isOpen={showTimeoutSOS}
          onClose={() => setShowTimeoutSOS(false)}
          participantNames={formState.participants.map(p => p.name)}
        />
        
        {/* Stay in Present Reminder */}
        <StayPresentReminder
          isOpen={showStayPresent}
          onClose={() => setShowStayPresent(false)}
        />
        
        {/* Role Play Suggestion */}
        <RolePlaySuggestion
          isOpen={showRolePlay}
          onClose={() => setShowRolePlay(false)}
          participantNames={formState.participants.map(p => p.name)}
        />
        
        {/* Instant Summary - Poetic NVC Translation */}
        <InstantSummary
          isOpen={showInstantSummary}
          onClose={() => setShowInstantSummary(false)}
          data={{
            title: formState.title,
            isAppreciation: experienceMode === 'appreciation',
            participants: formState.participants.map(p => ({
              id: p.id,
              name: p.name,
              observation: formState.participantInputs[p.id]?.blue || '',
              feelings: formState.participantInputs[p.id]?.red || '',
              needs: formState.participantInputs[p.id]?.green || '',
              empathyGuesses: formState.participantInputs[p.id]?.empathyGuesses || {},
              appreciationObservation: formState.participantInputs[p.id]?.appreciationObservation || '',
              appreciationFeeling: formState.participantInputs[p.id]?.appreciationFeeling || '',
              appreciationNeedMet: formState.participantInputs[p.id]?.appreciationNeedMet || ''
            })),
            gold: formState.gold
          }}
        />
        
        {/* Completion Dialog - Chapter 3 Option */}
        <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
          <DialogContent className="bg-gradient-to-b from-zinc-900 to-black border-amber-500/30 text-white max-w-md z-[100]">
            <DialogHeader>
              <DialogTitle className="text-2xl text-amber-400 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                <GoldIcon size={32} color="#FBBF24" className="mx-auto mb-2" />
                Gold Discovered!
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <p className="text-white/70 text-center">
                You've transformed conflict into understanding. Your project has been saved.
              </p>
              
              <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                <h3 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                  <CompassIcon size={18} color="#10B981" />
                  Continue Your Journey?
                </h3>
                <p className="text-white/60 text-sm">
                  Take your discoveries deeper with <span className="text-amber-400">Chapter 3: Vision Quest</span>. 
                  Define your core values, purpose, and mission based on what you've learned.
                </p>
                {getGreenNeedsForVisionQuest().length > 0 && (
                  <p className="text-emerald-300/70 text-xs mt-2">
                    Your green needs ({getGreenNeedsForVisionQuest().slice(0, 3).join(', ')}{getGreenNeedsForVisionQuest().length > 3 ? '...' : ''}) will carry forward as values.
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => handleCompletionChoice('vision')}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-medium py-3"
                >
                  <CompassIcon size={18} color="white" className="mr-2" />
                  Continue to Vision Quest
                </Button>
                
                <Button
                  onClick={() => handleCompletionChoice('board')}
                  variant="outline"
                  className="w-full border-white/20 text-white/70 hover:bg-white/10"
                >
                  <ArrowIcon size={16} color="#9CA3AF" direction="right" className="mr-2" />
                  Go to Project Board
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Golden Summary Dialog */}
        <GoldenSummary
          isOpen={showGoldenSummary}
          onClose={() => setShowGoldenSummary(false)}
          belongingData={{
            title: formState.title,
            participants: [
              // Include self first
              {
                id: 'self',
                name: formState.owner || 'You',
                observation: formState.participantInputs['self']?.blue || '',
                feelings: formState.participantInputs['self']?.red || '',
                needs: formState.participantInputs['self']?.green || '',
                empathyGuesses: formState.participantInputs['self']?.empathyGuesses || {},
                appreciationObservation: formState.participantInputs['self']?.appreciationObservation || '',
                appreciationFeeling: formState.participantInputs['self']?.appreciationFeeling || '',
                appreciationNeedMet: formState.participantInputs['self']?.appreciationNeedMet || ''
              },
              // Then other participants
              ...formState.participants.map(p => ({
                id: p.id,
                name: p.name,
                observation: formState.participantInputs[p.id]?.blue || '',
                feelings: formState.participantInputs[p.id]?.red || '',
                needs: formState.participantInputs[p.id]?.green || '',
                empathyGuesses: formState.participantInputs[p.id]?.empathyGuesses || {},
                appreciationObservation: formState.participantInputs[p.id]?.appreciationObservation || '',
                appreciationFeeling: formState.participantInputs[p.id]?.appreciationFeeling || '',
                appreciationNeedMet: formState.participantInputs[p.id]?.appreciationNeedMet || ''
              }))
            ],
            gold: formState.gold,
            isAppreciation: experienceMode === 'appreciation',
            isRomance: experienceMode === 'romance'
          }}
          visionData={null}
          includeVision={false}
          userId={localStorage.getItem('elchme_user_id')}
        />
      </div>
    );
  };

  // BOARD VIEW
  const renderBoard = () => {
    const columns = [
      { id: 'now', title: 'Active', accent: 'amber' },
      { id: 'next', title: 'Next', accent: 'blue' },
      { id: 'someday', title: 'Later', accent: 'white' },
      { id: 'done', title: 'Complete', accent: 'emerald' },
    ];

    return (
      <div className="min-h-screen bg-black p-4 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Button onClick={() => setView('landing')} variant="ghost" className="text-white/40 hover:text-white p-2">
                <ArrowIcon size={20} color="#9CA3AF" direction="left" />
              </Button>
              <h1 className="text-lg font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-amber-400">ElchmE</span> Journeys
              </h1>
            </div>
            <Button onClick={startNewProject} className="bg-amber-600 hover:bg-amber-500 text-black text-sm">
              <PlusIcon size={16} color="#10B981" className="mr-1" /> New
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {columns.map(column => (
              <div key={column.id} className="bg-white/5 rounded-xl p-4 min-h-[200px] border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-xs font-medium tracking-wide text-${column.accent}-400 uppercase`}>{column.title}</h3>
                  <Badge variant="outline" className="text-[10px] border-white/20 text-white/40">
                    {projects.filter(p => p.status === column.id).length}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {projects.filter(p => p.status === column.id).map(project => (
                    <Card 
                      key={project.id}
                      className="bg-black/50 border-white/10 hover:border-amber-500/30 cursor-pointer transition-all"
                      onClick={() => { setCurrentProject(project); setView('project-detail'); }}
                    >
                      <CardContent className="p-3">
                        <h4 className="text-white font-medium text-sm mb-1">{project.title}</h4>
                        <p className="text-amber-300/70 text-xs mb-2 line-clamp-2">{project.gold}</p>
                        <div className="flex justify-between items-center text-[10px] text-white/40">
                          <span><ProfileIcon size={12} color="#9CA3AF" className="inline mr-1" />{project.owner}</span>
                          <span><CalendarIcon size={12} color="#8B5CF6" className="inline mr-1" />{project.checkInDate}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {projects.filter(p => p.status === column.id).length === 0 && (
                    <div className="text-center py-8 text-white/20 text-xs">Empty</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Project Footer Navigation */}
        <ElchmEProjectFooter currentView="board" />
      </div>
    );
  };

  // PROJECT DETAIL
  const renderProjectDetail = () => {
    if (!currentProject) return null;
    const project = isEditingProject && editedProject ? editedProject : currentProject;
    const inputs = project.participantInputs || {};
    const participants = project.participants || [];

    return (
      <div className="min-h-screen bg-black p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Button onClick={() => { setView('board'); cancelEditingProject(); }} variant="ghost" className="text-white/40 hover:text-white">
              <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" /> Back
            </Button>
            <div className="flex gap-2">
              {!isEditingProject ? (
                <>
                  <Button onClick={startEditingProject} variant="ghost" className="text-white/40 hover:text-amber-400">
                    <PenIcon size={16} color="#F59E0B" className="mr-1" /> Edit
                  </Button>
                  <Button onClick={() => deleteProject(currentProject.id)} variant="ghost" className="text-white/30 hover:text-red-400">
                    <TrashIcon size={16} color="#EF4444" />
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={saveEditedProject} variant="ghost" className="text-emerald-400 hover:text-emerald-300">
                    <CheckIcon size={16} color="#4ADE80" className="mr-1" /> Save
                  </Button>
                  <Button onClick={cancelEditingProject} variant="ghost" className="text-white/40 hover:text-white">
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          <Card className="bg-white/5 border-white/10 mb-6">
            <CardHeader className="pb-2">
              {isEditingProject ? (
                <Input
                  value={editedProject?.title || ''}
                  onChange={(e) => updateEditedField('title', e.target.value)}
                  className="bg-black/30 border-amber-500/30 text-white text-lg font-medium"
                  placeholder="Conflict title..."
                />
              ) : (
                <CardTitle className="text-white text-lg flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <StarIcon size={20} color="#FBBF24" />{project.title}
                </CardTitle>
              )}
              {participants.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {participants.map((p, idx) => (
                    <Badge key={idx} variant="outline" className="border-purple-500/30 text-purple-300 text-[10px]">
                      {p.name || p}
                    </Badge>
                  ))}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Multi-person OFNR */}
              {participants.length > 0 ? (
                participants.map((person, idx) => (
                  <div key={idx} className="bg-black/30 rounded-lg p-3 border border-white/5">
                    <p className="text-amber-400 text-xs font-medium mb-2">{person.name || person}</p>
                    {isEditingProject ? (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[10px] text-blue-400 uppercase mb-1 block">Observation</label>
                          <Textarea
                            value={editedProject?.participantInputs?.[person.id]?.blue || ''}
                            onChange={(e) => updateEditedParticipantInput(person.id, 'blue', e.target.value)}
                            className="bg-blue-900/20 border-blue-500/30 text-white text-sm min-h-[60px]"
                            placeholder="What happened..."
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-red-400 uppercase mb-1 block">Feelings</label>
                          <Textarea
                            value={editedProject?.participantInputs?.[person.id]?.red || ''}
                            onChange={(e) => updateEditedParticipantInput(person.id, 'red', e.target.value)}
                            className="bg-red-900/20 border-red-500/30 text-white text-sm min-h-[60px]"
                            placeholder="How they feel..."
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-emerald-400 uppercase mb-1 block">Needs</label>
                          <Textarea
                            value={editedProject?.participantInputs?.[person.id]?.green || ''}
                            onChange={(e) => updateEditedParticipantInput(person.id, 'green', e.target.value)}
                            className="bg-emerald-900/20 border-emerald-500/30 text-white text-sm min-h-[60px]"
                            placeholder="What they need..."
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-xs">
                        <p className="text-blue-300/70"><ObserveIcon size={12} color="#60A5FA" className="inline mr-1" />{inputs[person.id]?.blue || '...'}</p>
                        <p className="text-red-300/70"><FeelIcon size={12} color="#F87171" className="inline mr-1" />{inputs[person.id]?.red || '...'}</p>
                        <p className="text-emerald-300/70"><NeedIcon size={12} color="#34D399" className="inline mr-1" />{inputs[person.id]?.green || '...'}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                isEditingProject ? (
                  <>
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-3">
                      <label className="text-[10px] text-blue-400 uppercase mb-1 block">Observation</label>
                      <Textarea
                        value={editedProject?.participantInputs?.['self']?.blue || editedProject?.blue || ''}
                        onChange={(e) => updateEditedParticipantInput('self', 'blue', e.target.value)}
                        className="bg-black/30 border-blue-500/30 text-white text-sm min-h-[60px]"
                        placeholder="What happened..."
                      />
                    </div>
                    <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-3">
                      <label className="text-[10px] text-red-400 uppercase mb-1 block">Feelings</label>
                      <Textarea
                        value={editedProject?.participantInputs?.['self']?.red || editedProject?.red || ''}
                        onChange={(e) => updateEditedParticipantInput('self', 'red', e.target.value)}
                        className="bg-black/30 border-red-500/30 text-white text-sm min-h-[60px]"
                        placeholder="How you feel..."
                      />
                    </div>
                    <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg p-3">
                      <label className="text-[10px] text-emerald-400 uppercase mb-1 block">Needs</label>
                      <Textarea
                        value={editedProject?.participantInputs?.['self']?.green || editedProject?.green || ''}
                        onChange={(e) => updateEditedParticipantInput('self', 'green', e.target.value)}
                        className="bg-black/30 border-emerald-500/30 text-white text-sm min-h-[60px]"
                        placeholder="What you need..."
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-[10px] text-blue-400 uppercase mb-1">Observation</p>
                      <p className="text-blue-100/80 text-sm">{inputs['self']?.blue || project.blue}</p>
                    </div>
                    <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-3">
                      <p className="text-[10px] text-red-400 uppercase mb-1">Feelings</p>
                      <p className="text-red-100/80 text-sm">{inputs['self']?.red || project.red}</p>
                    </div>
                    <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg p-3">
                      <p className="text-[10px] text-emerald-400 uppercase mb-1">Needs</p>
                      <p className="text-emerald-100/80 text-sm">{inputs['self']?.green || project.green}</p>
                    </div>
                  </>
                )
              )}
              
              <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3">
                <p className="text-[10px] text-amber-400 uppercase mb-1">Gold · Resolution</p>
                {isEditingProject ? (
                  <Textarea
                    value={editedProject?.gold || ''}
                    onChange={(e) => updateEditedField('gold', e.target.value)}
                    className="bg-black/30 border-amber-500/30 text-amber-100 text-sm min-h-[60px]"
                    placeholder="The golden resolution..."
                  />
                ) : (
                  <p className="text-amber-100 text-sm font-medium">{project.gold}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-4">
                {['now', 'next', 'someday', 'done'].map(status => (
                  <Button
                    key={status}
                    onClick={() => updateProjectStatus(currentProject.id, status)}
                    variant={project.status === status ? 'default' : 'outline'}
                    size="sm"
                    className={project.status === status ? 'bg-amber-600 text-black' : 'border-white/20 text-white/50'}
                    disabled={isEditingProject}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Export to SketchHub */}
              {!isEditingProject && (
                <div className="pt-4 border-t border-white/10 mt-4 space-y-3">
                  <Button
                    onClick={() => exportToSketchHub(currentProject)}
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-black font-medium"
                    data-testid="export-to-sketchhub-btn"
                  >
                    <PenIcon size={16} color="#000000" className="mr-2" />
                    Export to SketchHub
                  </Button>
                  
                  {/* Golden Summary - Belonging Journey */}
                  <GoldenSummaryButton
                    onClick={() => setShowGoldenSummary(true)}
                    label="View Golden Summary"
                  />
                  
                  {/* Continue to Vision Quest - Chapter 2 */}
                  <Button
                    onClick={launchVisionQuest}
                    className="w-full bg-gradient-to-r from-purple-600 to-rose-600 hover:from-purple-500 hover:to-rose-500 text-white font-medium"
                    data-testid="launch-vision-quest-btn"
                  >
                    <StarIcon size={16} color="#FFFFFF" className="mr-2" />
                    Continue to Chapter 2: Vision Quest
                  </Button>
                  
                  <p className="text-white/30 text-xs text-center mt-2">
                    Define your core values, purpose, and mission
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="bg-white/5 border-white/10 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <MessageIcon size={16} color="rgba(255,255,255,0.4)" /> Family Thread
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                {(currentProject.messages || []).map(msg => (
                  <div key={msg.id} className="bg-black/30 rounded-lg p-3">
                    <p className="text-white/80 text-sm">{msg.text}</p>
                    <p className="text--[10px] text-white/30 mt-1">{msg.author} · {new Date(msg.timestamp).toLocaleDateString()}</p>
                  </div>
                ))}
                {(!currentProject.messages || currentProject.messages.length === 0) && (
                  <p className="text-white/20 text-xs text-center py-4">No messages yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Add a message..."
                  className="bg-black/30 border-white/10 text-white text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && addMessage(currentProject.id, newMessage)}
                />
                <Button onClick={() => addMessage(currentProject.id, newMessage)} className="bg-white/10 hover:bg-white/20">
                  <PlusIcon size={16} color="#10B981" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lore */}
          {currentProject.status === 'done' && (
            <Card className="bg-amber-900/10 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-amber-300 text-sm flex items-center gap-2">
                  <WisdomIcon size={16} color="#F472B6" /> Wisdom Gathered
                </CardTitle>
              </CardHeader>
              <CardContent>
                {currentProject.lore ? (
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-amber-100/80 text-sm leading-relaxed">{currentProject.lore.text}</p>
                  </div>
                ) : (
                  showLoreForm ? (
                    <div className="space-y-3">
                      <Textarea
                        value={loreInput}
                        onChange={(e) => setLoreInput(e.target.value)}
                        placeholder="What wisdom emerged?"
                        className="bg-black/30 border-amber-500/20 text-white min-h-[100px]"
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => addLore(currentProject.id, loreInput)} className="bg-amber-600 hover:bg-amber-500 text-black">
                          <SaveIcon size={16} color="#F59E0B" className="mr-2" /> Save
                        </Button>
                        <Button onClick={() => setShowLoreForm(false)} variant="ghost" className="text-white/40">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={() => setShowLoreForm(true)} variant="outline" className="w-full border-amber-500/30 text-amber-300 hover:bg-amber-900/20">
                      <WisdomIcon size={16} color="#F472B6" className="mr-2" /> Record Wisdom
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  // LORE VIEW
  const renderLore = () => {
    const completedWithLore = projects.filter(p => p.status === 'done' && p.lore);
    return (
      <div className="min-h-screen bg-black p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <Button onClick={() => setView('landing')} variant="ghost" className="text-white/40 hover:text-white">
              <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" /> Back
            </Button>
            <h1 className="text-lg font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="text-amber-400">ElchmE</span> Wisdom
            </h1>
          </div>
          <div className="space-y-4">
            {completedWithLore.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <BookIcon size={40} color="rgba(255,255,255,0.2)" className="mx-auto mb-4" />
                  <p className="text-white/40 text-sm">No wisdom recorded yet</p>
                </CardContent>
              </Card>
            ) : (
              completedWithLore.map(project => (
                <Card key={project.id} className="bg-amber-900/10 border-amber-500/20">
                  <CardContent className="p-6">
                    <h3 className="text-amber-300 font-medium text-sm mb-2">{project.title}</h3>
                    <p className="text-amber-100/70 text-sm mb-4 leading-relaxed">{project.lore.text}</p>
                    <div className="flex justify-between items-center text-[10px] text-white/30">
                      <span>Gold: {project.gold.substring(0, 40)}...</span>
                      <span>{new Date(project.lore.createdAt).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const content = (() => {
    switch (view) {
      case 'new-project': return renderNewProject();
      case 'board': return renderBoard();
      case 'project-detail': return renderProjectDetail();
      case 'lore': return renderLore();
      default: return renderLanding();
    }
  })();

  return (
    <>
      {content}
      {/* Admin Feedback Button - available throughout the app */}
      <AdminFeedbackButton position="bottom-right" />
      
      {/* Soul Score Widget */}
      <SoulScoreWidget
        isOpen={showSoulScore}
        onClose={() => setShowSoulScore(false)}
        checkpoint={soulScoreCheckpoint}
        userId={localStorage.getItem('elchme_user_id')}
        sessionId={sessionId}
        onComplete={handleSoulScoreComplete}
        onSkip={handleSoulScoreSkip}
      />
    </>
  );
}
